import React, { useState, useRef, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './home.css';
import './home-light.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { href, useNavigate } from 'react-router-dom';
import LoggedOutHome from './LoggedOutHome';
import CustomPopup from './CustomPopup';
import logo from '../images/logo.png';

function Home() {
  // State management
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth >= 768);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [copiedCodeId, setCopiedCodeId] = useState(null);
  const [downloadedCodeId, setDownloadedCodeId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [editingChatId, setEditingChatId] = useState(null);
  const [tempTitle, setTempTitle] = useState('');
  const [renameSource, setRenameSource] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [Userinfo, setUserinfo] = useState([]);
  const [UserImage, SetUserImage] = useState();
  const navigate = useNavigate();
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [theme, setTheme] = useState('Dark');
  const [layout, setLayout] = useState('Normal');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const messagesContainerRef = useRef(null);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [showRemoveProfileModal, setShowRemoveProfileModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // General settings state
  const [generalSettings, setGeneralSettings] = useState({
    language: 'English',
    darkMode: true,
    fontSize: 'Medium'
  });

  // Refs
  const profilePicInputRef = useRef(null);
  const bottomRef = useRef(null);
  const messagesEndRef = useRef(null);
  const userId = useRef("user_" + Math.random().toString(36).substr(2, 9));
  const typingIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const botMessageIdRef = useRef(null);
  const inputRef = useRef(null);
  const sidebarRef = useRef(null);
  const typingMessageId = useRef(null);
  const fileInputRef = useRef(null);
  const renameInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const customizeModalRef = useRef(null);
  const settingsModalRef = useRef(null);
  const helpModalRef = useRef(null);
  const [userCredits, setUserCredits] = useState({
    total: 60,
    used: 0,
    remaining: 60,
    plan: 'free'
  });

  const [creditsLoading, setCreditsLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));
  const user_id = user?.user_id;

  useEffect(() => {
      if (!user_id) {
        navigate('/');
      }
    }, [user_id, navigate]);

  const TYPING_SPEED = 5; // milliseconds per character
  const MAX_IMAGES = 3;

  // Generate unique ID for messages
  const generateUniqueId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const toggleTheme = () => {
    const newTheme = theme === 'Dark' ? 'Light' : 'Dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    // Apply theme class to body
    document.body.classList.remove('theme-dark', 'theme-light');
    document.body.classList.add(`theme-${newTheme.toLowerCase()}`);
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'Dark';
    setTheme(savedTheme);
    document.body.classList.remove('theme-dark', 'theme-light');
    if (savedTheme === 'System') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.body.classList.add(prefersDark ? 'theme-dark' : 'theme-light');
    } else {
      document.body.classList.add(`theme-${savedTheme.toLowerCase()}`);
    }
    setGeneralSettings(prev => ({
      ...prev,
      darkMode: savedTheme === 'Dark'
    }));
  }, []);

  // Effects
  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      // Clean up object URLs
      selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
    };
  }, [selectedImages]);

  useEffect(() => {
    if (inputValue === '' && inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  }, [inputValue]);


  useEffect(() => {
    // Focus input when not processing
    if (!isProcessing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isProcessing]);

  useEffect(() => {
    if (chatHistory.length === 0) {
      const defaultChat = {
        id: generateUniqueId(),
        title: 'New Chat',
        messages: [],
        createdAt: new Date().toISOString()
      };
      setChatHistory([defaultChat]);
      setActiveChat(defaultChat.id);
    }
  }, []);

  useEffect(() => {
    // Close sidebar when clicking outside
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        if (window.innerWidth < 768) {
          setIsSidebarVisible(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarVisible(false);
      } else {
        setIsSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const SWIPE_THRESHOLD = 50;
    const VERTICAL_TOLERANCE = 70;

    const handleTouchStart = (e) => {
      touchStartXRef.current = e.touches[0].clientX;
      touchStartYRef.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      if (touchStartXRef.current === null) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchEndX - touchStartXRef.current;
      const deltaY = Math.abs(touchEndY - touchStartYRef.current);

      if (deltaY > VERTICAL_TOLERANCE) {
        touchStartXRef.current = null;
        return;
      }

      if (window.innerWidth < 768) {
        // Left to right swipe - OPEN sidebar
        if (deltaX > SWIPE_THRESHOLD) {
          setIsSidebarVisible(true);
        }
        // Right to left swipe - CLOSE sidebar
        if (deltaX < -SWIPE_THRESHOLD && isSidebarVisible) {
          setIsSidebarVisible(false);
        }
      }
      touchStartXRef.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isSidebarVisible]);

  useEffect(() => {
    const fetchuserinfo = async () => {
      const BACKEND_URL = 'https://chatbot-ai-m3th.onrender.com';
      try {
        const response = await fetch(`${BACKEND_URL}/get_user_info/${user_id}`);
        const data = await response.json();

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'server error');
        }

        let profilePicUrl = null;

        // 🔥 FIX: Only get signed URL if it's an internal file (not external URL)
        if (data.profile_picture && !data.is_external_picture) {
          try {
            const signedUrlResponse = await fetch(`${BACKEND_URL}/get-signed-url`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                file_path: data.profile_picture,
                user_id: user_id
              })
            });

            if (signedUrlResponse.ok) {
              const result = await signedUrlResponse.json();
              if (result.signed_url) {
                profilePicUrl = result.signed_url;
              }
            }
          } catch (signedUrlError) {
            console.warn('Error getting signed URL:', signedUrlError);
          }
        } else if (data.profile_picture) {
          // It's an external URL (Google picture) - use directly
          profilePicUrl = data.profile_picture;
        }

        setUserinfo(data);

        const resolvedPicture =
          profilePicUrl ||
          data.google_profile?.picture_url ||
          null;

        SetUserImage({
          ...data.google_profile,
          picture_url: resolvedPicture
        });

      } catch (e) {
        setError(e.message || 'Error fetching user info!')
      } finally {
        setLoading(false);
      }
    }
    if (user_id) fetchuserinfo();
  }, [user_id]);

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const res = await fetch(`https://chatbot-ai-m3th.onrender.com/get_user_chats/${user_id}`);
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.detail || "Server error");
        }
        const data = await res.json();
        setChats(data.chats);

      } catch (err) {
        setError(err.message || "Error fetching chats");
      } finally {
        setLoading(false);
      }
    };

    if (user_id) fetchChats();
  }, [user_id]);

  const isUserNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const threshold = 100; // pixels from bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= threshold;
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      showPopup(
        'Invalid File',
        'Please select a valid image file (JPEG, PNG, WEBP)',
        'error',
        'center'
      );
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      showPopup(
        'File Too Large',
        'File size should be less than 2MB',
        'error',
        'center'
      );
      return;
    }

    setIsUploadingProfile(true);

    try {
      const formData = new FormData();
      formData.append('profile_pic', file);
      formData.append('user_id', user_id);

      const BACKEND_URL = 'https://chatbot-ai-m3th.onrender.com';
      const response = await fetch(`${BACKEND_URL}/upload-profile-pic`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload profile picture');
      }

      const result = await response.json();

      // FIX: Construct full URL
      const fullProfileUrl = result.profile_url.startsWith('http')
        ? result.profile_url
        : `${BACKEND_URL}${result.profile_url}`;

      // Update local state with new profile picture
      SetUserImage({
        ...UserImage,
        picture_url: fullProfileUrl
      });

      showPopup(
        'Success',
        'Profile picture updated successfully!',
        'success',
        'top'
      );

    } catch (error) {
      showPopup(
        'Upload Failed',
        'Failed to upload profile picture',
        'error',
        'center'
      );
    } finally {
      setIsUploadingProfile(false);
      if (profilePicInputRef.current) {
        profilePicInputRef.current.value = '';
      }
    }
  };

  // Remove profile picture function - ab sirf modal open karega
  const handleRemoveProfilePic = () => {
    if (!UserImage?.picture_url) {
      showPopup(
        'No Picture',
        'No profile picture to remove',
        'info',
        'center'
      );
      return;
    }
    setShowRemoveProfileModal(true);
  };

  // Actual remove function
  const confirmRemoveProfilePic = async () => {
    try {
      const BACKEND_URL = 'https://chatbot-ai-m3th.onrender.com';
      const response = await fetch(`${BACKEND_URL}/remove-profile-pic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user_id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove profile picture');
      }

      // Update local state - remove profile picture
      SetUserImage({
        ...UserImage,
        picture_url: null
      });

      setShowRemoveProfileModal(false);

      showPopup(
        'Success',
        'Profile picture removed successfully!',
        'success',
        'top'
      );


    } catch (error) {
      showPopup(
        'Remove Failed',
        'Failed to remove profile picture',
        'error',
        'center'
      );
      setShowRemoveProfileModal(false);
    }
  };

  // Modified scrollToBottom function
  const scrollToBottom = (force = false) => {
    if (force || shouldAutoScroll) {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        });
      }
    }
  };

  // Update useEffect for messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const nearBottom = isUserNearBottom();
      setShouldAutoScroll(nearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Force scroll when sending new message
  const forceScrollToBottom = () => {
    setShouldAutoScroll(true);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  };

  const fetchUserCredits = async () => {
    if (!user_id) return;

    try {
      setCreditsLoading(true);
      const response = await fetch(`https://chatbot-ai-m3th.onrender.com/user-credits/${user_id}`);
      if (!response.ok) throw new Error('Failed to fetch credits');

      const data = await response.json();

      if (data.remaining_credits <= 0) {
        // Use popup instead of toast
        showPopup(
          'Daily Limit Reached',
          `You've used ${data.used_credits}/${data.total_credits} chats today. Your credits will reset tomorrow.`,
          'error',
          'center'
        );
        return false;
      }

      setUserCredits({
        total: data.total_credits,
        used: data.used_credits,
        remaining: data.remaining_credits,
        plan: data.plan_type
      });

    } catch (error) {
    } finally {
      setCreditsLoading(false);
    }
  };

  // Credits auto-refresh timer
  useEffect(() => {
    if (!user_id) return;

    // Initial fetch
    fetchUserCredits();

    // Refresh every 300 seconds
    const intervalId = setInterval(() => {
      fetchUserCredits();
    }, 300000);

    return () => clearInterval(intervalId);
  }, [user_id]);

  const [popupConfig, setPopupConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error',
    display: 'center'
  });

  useEffect(() => {
    const anyModalOpen = showSettingsModal || showCustomizeModal || showHelpModal || showDeleteModal || showRemoveProfileModal || showLogoutModal;

    if (anyModalOpen) {
      setIsSidebarVisible(false);
    } else {
      if (window.innerWidth >= 768) {
        setIsSidebarVisible(true);
      }
    }
  }, [showSettingsModal, showCustomizeModal, showHelpModal, showDeleteModal, showRemoveProfileModal, showLogoutModal]);

  // Function to show popup
  const showPopup = (title, message, type = 'error', display = 'center') => {
    setPopupConfig({
      isOpen: true,
      title,
      message,
      type,
      display
    });
  };

  // Function to close popup
  const closePopup = () => {
    setPopupConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Credits check function में popup use करें
  const checkCreditsBeforeSend = async () => {
    if (!user_id) return true;

    try {
      const response = await fetch(`https://chatbot-ai-m3th.onrender.com/user-credits/${user_id}`);

      if (response.status === 404 || response.status === 500) {
        return true;
      }

      if (!response.ok) return true;

      const data = await response.json();

      if (data.remaining_credits <= 0) {
        // Use popup instead of toast
        showPopup(
          'Daily Limit Reached',
          `You've used ${data.used_credits}/${data.total_credits} chats today. Your credits will reset tomorrow.`,
          'error',
          'center'
        );
        return false;
      }

      setUserCredits({
        total: data.total_credits || 60,
        used: data.used_credits || 0,
        remaining: data.remaining_credits || 60,
        plan: data.plan_type || 'free'
      });

      return true;
    } catch (error) {
      return true;
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setShowLogoutModal(false);
    navigate('/');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Image handling functions
  // Image handling functions
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    // Validate file types
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    const invalidFiles = files.filter(f => !validTypes.includes(f.type));

    if (invalidFiles.length > 0) {
      showPopup(
        'Invalid File Type',
        `Invalid file type: ${invalidFiles.map(f => f.name).join(', ')}. Only JPEG, PNG, and WEBP are allowed.`,
        'error',
        'center'
      );
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter(f => f.size > maxSize);

    if (oversizedFiles.length > 0) {
      showPopup(
        'Files Too Large',
        `Files too large (max 5MB): ${oversizedFiles.map(f => f.name).join(', ')}`,
        'error',
        'center'
      );
      return;
    }

    // Check total images limit
    if (files.length + selectedImages.length > MAX_IMAGES) {
      showPopup(
        'Image Limit Exceeded',
        `You can upload maximum ${MAX_IMAGES} images. Currently selected: ${selectedImages.length}, trying to add: ${files.length}`,
        'warning',
        'center'
      );
      return;
    }

    // Create image objects
    const newImages = files.map(file => ({
      file: file, // IMPORTANT: Store the actual File object
      id: generateUniqueId(),
      preview: URL.createObjectURL(file)
    }));

    setSelectedImages(prev => [...prev, ...newImages]);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // In your removeImage function
  const removeImage = (id) => {
    const imageToRemove = selectedImages.find(img => img.id === id);
    if (imageToRemove?.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    setSelectedImages(prev => prev.filter(img => img.id !== id));
  };

  const clearSelectedImages = () => {
    selectedImages.forEach(img => {
      if (img.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(img.preview);
      }
    });
    setSelectedImages([]);
  };

  // Home.js mein uploadImages function ko replace karein
  const uploadImages = async () => {
    if (selectedImages.length === 0) return null;

    if (!inputValue.trim()) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      return null;
    }

    setIsUploadingImages(true);

    try {
      const formData = new FormData();

      // IMPORTANT: Append files with correct field name
      selectedImages.forEach((image, index) => {
        // Make sure image.file exists and is a File object
        if (image.file instanceof File) {
          formData.append('images', image.file);
        } else {
          console.error('Invalid file object:', image);
        }
      });

      // Append other data
      formData.append('user_id', user_id || userId.current);
      formData.append('chat_id', activeChat || 'default');
      if (inputValue.trim()) {
        formData.append('prompt', inputValue.trim());
      }

      // IMPORTANT: Use the correct backend URL (production URL)
      const BACKEND_URL = 'https://chatbot-ai-m3th.onrender.com';

      const response = await fetch(`${BACKEND_URL}/upload-image`, {
        method: 'POST',
        // DO NOT set Content-Type header - browser will set it with boundary
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.status === 'error') {
        throw new Error(result.error || `Upload failed with status ${response.status}`);
      }

      return result;
    } catch (error) {
      showPopup(
        'Upload Failed',
        error.message || 'Failed to upload images',
        'error',
        'center'
      );
      return null;
    } finally {
      setIsUploadingImages(false);
    }
  };

  // Chat operations
  const generateChatTitle = (messages) => {
    const firstUserMessage = messages.find(msg => msg.sender === 'user');
    if (firstUserMessage) {
      const shortText = firstUserMessage.text
        .replace(/\n/g, ' ')
        .trim()
        .slice(0, 30);
      return shortText + (firstUserMessage.text.length > 30 ? '...' : '');
    }
    return `New Chat ${new Date().toLocaleTimeString()}`;
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedMessageId(id);
    setTimeout(() => setCopiedMessageId(null), 1000);
  };

  const handleCopyCode = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopiedCodeId(id);
    setTimeout(() => setCopiedCodeId(null), 1000);
  };

  const handleDownloadCode = (content, lang, id) => {
    // Language to file extension mapping
    const extensionMap = {
      // Web
      'javascript': 'js',
      'js': 'js',
      'jsx': 'jsx',
      'typescript': 'ts',
      'ts': 'ts',
      'tsx': 'tsx',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'sass': 'sass',
      'json': 'json',
      'xml': 'xml',
      'svg': 'svg',

      // Backend
      'python': 'py',
      'py': 'py',
      'java': 'java',
      'c': 'c',
      'cpp': 'cpp',
      'c++': 'cpp',
      'csharp': 'cs',
      'cs': 'cs',
      'go': 'go',
      'golang': 'go',
      'rust': 'rs',
      'rs': 'rs',
      'php': 'php',
      'ruby': 'rb',
      'rb': 'rb',
      'perl': 'pl',
      'pl': 'pl',
      'scala': 'scala',
      'kotlin': 'kt',
      'kt': 'kt',
      'swift': 'swift',

      // Shell/Database
      'bash': 'sh',
      'sh': 'sh',
      'shell': 'sh',
      'zsh': 'sh',
      'powershell': 'ps1',
      'ps1': 'ps1',
      'sql': 'sql',
      'mysql': 'sql',
      'postgresql': 'sql',
      'mongodb': 'js',

      // Config/Markup
      'yaml': 'yml',
      'yml': 'yml',
      'toml': 'toml',
      'ini': 'ini',
      'env': 'env',
      'dockerfile': 'dockerfile',
      'docker': 'dockerfile',
      'makefile': 'makefile',
      'markdown': 'md',
      'md': 'md',
      'mdx': 'mdx',
      'latex': 'tex',
      'tex': 'tex',

      // Data Science
      'r': 'r',
      'matlab': 'm',
      'julia': 'jl',
      'jl': 'jl',

      // Functional
      'haskell': 'hs',
      'hs': 'hs',
      'elixir': 'ex',
      'ex': 'ex',
      'erlang': 'erl',
      'erl': 'erl',
      'clojure': 'clj',
      'clj': 'clj',
      'fsharp': 'fs',
      'fs': 'fs',

      // Others
      'lua': 'lua',
      'dart': 'dart',
      'vim': 'vim',
      'groovy': 'groovy',
      'objective-c': 'm',
      'objc': 'm',
      'vb': 'vb',
      'vba': 'vba',
      'assembly': 'asm',
      'asm': 'asm',
    };

    // Clean and normalize language string
    let normalizedLang = '';
    if (lang) {
      normalizedLang = lang.toLowerCase().trim();
      // Remove any extra spaces or special characters
      normalizedLang = normalizedLang.replace(/[^a-z0-9+#-]/g, '');
    }

    // Get file extension
    let fileExtension = extensionMap[normalizedLang];

    // Handle special cases
    if (!fileExtension) {
      // Check if it's a compound like "c++"
      if (normalizedLang.includes('c++') || normalizedLang === 'cpp') {
        fileExtension = 'cpp';
      } else if (normalizedLang.includes('c#')) {
        fileExtension = 'cs';
      } else if (normalizedLang.includes('f#')) {
        fileExtension = 'fs';
      } else {
        // Default to txt for unknown languages
        fileExtension = 'txt';
      }
    }

    // Special handling for specific file types
    let mimeType = 'text/plain';
    if (fileExtension === 'json') {
      mimeType = 'application/json';
    } else if (fileExtension === 'xml' || fileExtension === 'svg') {
      mimeType = 'application/xml';
    } else if (fileExtension === 'html') {
      mimeType = 'text/html';
    } else if (fileExtension === 'css') {
      mimeType = 'text/css';
    } else if (fileExtension === 'js' || fileExtension === 'jsx' || fileExtension === 'ts' || fileExtension === 'tsx') {
      mimeType = 'application/javascript';
    } else if (fileExtension === 'py') {
      mimeType = 'text/x-python';
    } else if (fileExtension === 'java') {
      mimeType = 'text/x-java';
    }

    // Create filename
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    let fileName = `code-${normalizedLang || 'snippet'}-${timestamp}.${fileExtension}`;

    // Clean filename
    fileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');

    try {
      // Create and download blob
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 100);

      // Show success feedback
      setDownloadedCodeId(id);
      setTimeout(() => setDownloadedCodeId(null), 1500);

      showPopup(
        'Download Complete',
        `Downloaded: ${fileName}`,
        'success',
        'top'
      );

    } catch (error) {
      showPopup(
        'Download Failed',
        'Failed to download file',
        'error',
        'center'
      );
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const cancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);

    if (typingMessageId.current) {
      setMessages(prev => prev.filter(msg => msg.id !== typingMessageId.current));
      typingMessageId.current = null;
    }

    botMessageIdRef.current = null;
    typingMessageId.current = null;
    setIsProcessing(false);
    setIsTyping(false);
    setMessages(prev => prev.filter(msg => msg.id !== typingMessageId.current));
  };

  const startNewChat = () => {
    if (isTyping || isProcessing) return;

    const emptyChat = chatHistory.find(chat => chat.messages.length === 0);
    if (emptyChat) {
      setActiveChat(emptyChat.id);
      setMessages([]);
    } else {
      const currentChat = chatHistory.find(chat => chat.id === activeChat);
      const hasBotMessage = messages.some(msg => msg.sender === 'bot');

      if (currentChat && hasBotMessage) {
        const updatedChat = {
          ...currentChat,
          messages: [...messages],
          title: currentChat.title === 'New Chat' ? generateChatTitle(messages) : currentChat.title,
          updatedAt: new Date().toISOString()
        };

        const updatedHistory = chatHistory.map(chat =>
          chat.id === activeChat ? updatedChat : chat
        );
        setChatHistory(updatedHistory);
      }

      const newChat = {
        id: generateUniqueId(),
        title: 'New Chat',
        messages: [],
        createdAt: new Date().toISOString()
      };
      setChatHistory(prev => [newChat, ...prev]);
      setMessages([]);
      setActiveChat(newChat.id);
    }

    // Auto-close sidebar on mobile
    if (window.innerWidth < 768) {
      setIsSidebarVisible(false);
    }
  };

  const loadChat = async (chat) => {
    try {
      const BACKEND_URL = 'https://chatbot-ai-m3th.onrender.com';

      // 🔥 FIX: Pehle turant messages load karo WITHOUT waiting for images
      const transformedMessages = (chat.messages || []).flatMap(msg => {
        const messages = [];

        // Add user message
        if (msg.prompt) {
          const hasImages = (msg.image_urls && Array.isArray(msg.image_urls) && msg.image_urls.length > 0) ||
            (msg.images && Array.isArray(msg.images) && msg.images.length > 0) ||
            msg.type === 'image-upload';

          // 🔥 IMPORTANT: Pehle empty image_urls ke saath message add karo
          // Images baad mein async load hongi
          messages.push({
            id: (msg.message_id || generateUniqueId()) + '_user',
            text: msg.prompt,
            sender: 'user',
            type: hasImages ? 'image-upload' : 'text',
            timestamp: msg.timestamp || new Date().toISOString(),
            image_urls: [], // Empty initially
            images: [], // Empty initially
            _rawImageUrls: msg.image_urls || msg.images || [] // Store raw URLs for later
          });
        }

        // Add bot response
        if (msg.response) {
          messages.push({
            id: (msg.message_id || generateUniqueId()) + '_bot',
            text: msg.response,
            sender: 'bot',
            type: msg.type || 'text',
            timestamp: msg.timestamp || new Date().toISOString()
          });
        }

        return messages;
      });

      // Sort by timestamp
      transformedMessages.sort((a, b) =>
        new Date(a.timestamp) - new Date(b.timestamp)
      );

      // 🔥 Turant messages set karo (without waiting for images)
      setMessages(transformedMessages);
      setActiveChat(chat.chat_id);

      // Auto-close sidebar on mobile
      if (window.innerWidth < 768) {
        setIsSidebarVisible(false);
      }

      // 🔥 Ab background mein images load karo
      // Find all user messages that have images
      const messagesWithImages = transformedMessages.filter(msg =>
        msg.sender === 'user' && msg._rawImageUrls && msg._rawImageUrls.length > 0
      );

      if (messagesWithImages.length > 0) {
        // Process each message's images in background
        for (const msg of messagesWithImages) {
          const rawUrls = msg._rawImageUrls;

          // Load images one by one or in parallel (parallel is faster)
          // In loadChat function, image loading section
          const processedUrls = await Promise.all(
            rawUrls.map(async (url) => {
              if (!url) return null;
              if (url.startsWith('http://') || url.startsWith('https://')) return url;
              if (url.startsWith('/')) return `${BACKEND_URL}${url}`;

              // Try to get signed URL
              try {
                const r = await fetch(`${BACKEND_URL}/get-signed-url`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ file_path: url, user_id: user_id }),
                });
                if (r.ok) {
                  const { signed_url } = await r.json();
                  return signed_url || null;
                } else {
                  // 🔥 FIX: Silently fail for missing images
                  console.warn(`Image not found: ${url}`);
                  return null;
                }
              } catch (e) {
                console.warn(`Error getting signed URL for ${url}:`, e.message);
                return null;
              }
            })
          );

          const validUrls = processedUrls.filter(Boolean);

          // 🔥 Update this specific message with loaded images
          setMessages(prev => prev.map(m =>
            m.id === msg.id
              ? { ...m, image_urls: validUrls, images: validUrls, _rawImageUrls: undefined }
              : m
          ));
        }
      }

    } catch (error) {
      console.error('Load chat error:', error);
      showPopup(
        'Load Failed',
        'Failed to load chat',
        'error',
        'center'
      );
    }
  };

  const handleShare = async (chatId) => {
    const chat = chats.find(c => c.id || c.chat_id === chatId);

    if (!chat) return;

    try {
      const content = chat.messages.map(msg => {
        const promptText = msg.prompt ? `You: ${msg.prompt}` : '';
        const responseText = msg.response ? `Assistant: ${msg.response}` : '';
        return [promptText, responseText].filter(Boolean).join('\n');
      }).join('\n\n');


      await navigator.clipboard.writeText(content);
      showPopup(
        'Success',
        'Chat copied to clipboard',
        'success',
        'top'
      );
    } catch (err) {
      showPopup(
        'Copy Failed',
        'Failed to copy chat',
        'error',
        'center'
      );
    }
  };

  const confirmDelete = (chatId) => {
    setChatToDelete(chatId);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!chatToDelete) return;

    try {
      const response = await fetch('https://chatbot-ai-m3th.onrender.com/delete-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          user_id: user_id,
          chat_id: chatToDelete,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete chat');
      }

      // Update local state immediately
      setChats(prev => prev.filter(chat => chat.chat_id !== chatToDelete));

      if (activeChat === chatToDelete) {
        setMessages([]);
        setActiveChat(null);
      }

      showPopup(
        'Deleted',
        'Chat deleted successfully',
        'success',
        'top'
      );
    } catch (error) {
      showPopup(
        'Delete Failed',
        'Failed to delete chat',
        'error',
        'center'
      );
    } finally {
      setShowDeleteModal(false);
      setChatToDelete(null);
    }
  };

  const handleRenameInline = async (chatId) => {
    if (!tempTitle.trim()) {
      setEditingChatId(null);
      setTempTitle('');
      setRenameSource(null);
      return;
    }

    try {
      setChats(prev =>
        prev.map(chat =>
          chat.chat_id === chatId
            ? { ...chat, title: tempTitle.trim(), updatedAt: new Date().toISOString() }
            : chat
        )
      );

      await fetch('https://chatbot-ai-m3th.onrender.com/rename-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          user_id: user_id,
          chat_id: chatId,
          new_title: tempTitle.trim(),
        }),
      });

    } catch (err) {
      showPopup(
        'Rename Failed',
        'Failed to rename chat',
        'error',
        'center'
      );
      // Revert local changes if API fails
      setChats(prev => prev.map(chat =>
        chat.chat_id === chatId
          ? { ...chat, title: chat.title } // revert to original title
          : chat
      ));
    }

    setEditingChatId(null);
    setTempTitle('');
    setRenameSource(null);
  };

  const updateChatHistory = (userMessage, botMessage, title) => {
    if (!activeChat) {
      const newChat = {
        id: generateUniqueId(),
        title,
        messages: [userMessage, botMessage],
        createdAt: new Date().toISOString()
      };
      setChatHistory(prev => [newChat, ...prev]);
      setActiveChat(newChat.id);
    } else {
      setChatHistory(prev =>
        prev.map(chat =>
          chat.id === activeChat
            ? {
              ...chat,
              title: chat.title === 'New Chat' ? title : chat.title,
              messages: [...chat.messages, userMessage, botMessage],
              updatedAt: new Date().toISOString()
            }
            : chat
        )
      );
    }
  };

  const fetchChats = async () => {
    try {
      const res = await fetch(`https://chatbot-ai-m3th.onrender.com/get_user_chats/${user_id}`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Server error");
      }
      const data = await res.json();
      setChats(data.chats);

      // If there's a new chat, set it as active
      if (data.chats.length > 0 && !activeChat) {
        setActiveChat(data.chats[0].chat_id);
      }
    } catch (err) {
      setError(err.message || "Error fetching chats");
    } finally {
      setLoading(false);
    }
  };

  const MAX_MESSAGE_LENGTH = 10000;

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (isProcessing || isTyping) return;

    if ((!inputValue.trim() && selectedImages.length === 0) || isProcessing) return;

    if (!inputValue.trim()) {
      if (selectedImages.length > 0) {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
      return;
    }

    if (messages.filter(m => m.type !== 'typing').length >= 40) {
      return;
    }

    if (inputValue.trim().length > MAX_MESSAGE_LENGTH) {
      showPopup(
        'Message Too Large',
        `Your message is too long (${inputValue.trim().length} characters). Please keep it under ${MAX_MESSAGE_LENGTH} characters.`,
        'error',
        'center'
      );
      return;
    }

    if (user_id) {
      const hasCredits = await checkCreditsBeforeSend();
      if (!hasCredits) {
        return;
      }
    }

    const userMessageText = inputValue.trim() || (selectedImages.length > 0 ? "Analyze these images" : "");
    if (!userMessageText) return;

    const userMessage = {
      id: generateUniqueId(),
      text: userMessageText,
      type: selectedImages.length > 0 ? 'image-upload' : 'text',
      sender: 'user',
      timestamp: new Date().toISOString(),
      images: selectedImages.map(img => img.preview) || [],
      image_urls: []
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');

    const uploadedImages = [...selectedImages];
    if (selectedImages.length > 0) {
      clearSelectedImages();
    }

    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    setIsProcessing(true);
    setIsTyping(true);

    const typingMsgId = generateUniqueId();
    typingMessageId.current = typingMsgId;

    setMessages(prev => [...prev, {
      id: typingMsgId,
      type: 'typing',
      sender: 'bot',
      text: '',
      timestamp: new Date().toISOString()
    }]);

    abortControllerRef.current = new AbortController();
    const title = generateChatTitle([userMessage]);

    try {
      let response;

      const BACKEND_URL = 'https://chatbot-ai-m3th.onrender.com';

      // Handle image upload
      if (uploadedImages.length > 0) {
        const uploadResponse = await uploadImages();

        if (!uploadResponse) throw new Error('Image upload failed');

        // 🔥 FIX: Turant signed URLs fetch karo aur user message update karo
        const resolvedUrls = await Promise.all(
          (uploadResponse.image_urls || []).map(async (url) => {
            if (!url) return null;
            if (url.startsWith('http://') || url.startsWith('https://')) return url;

            try {
              const r = await fetch(`${BACKEND_URL}/get-signed-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ file_path: url, user_id: user_id }),
              });
              if (r.ok) {
                const { signed_url } = await r.json();
                return signed_url;
              }
            } catch (e) {
              console.error('Signed URL error:', e);
            }
            return null;
          })
        );

        const validUrls = resolvedUrls.filter(Boolean);

        // 🔥 FIX: Turant messages update karo with proper URLs
        setMessages(prev => prev.map(msg =>
          msg.id === userMessage.id
            ? { ...msg, image_urls: validUrls, images: validUrls, type: 'image-upload' }
            : msg
        ));

        const imageResponses = uploadResponse.llm_responses;
        const responseText = imageResponses && imageResponses.length > 0
          ? imageResponses[0]
          : "Image processed.";

        const botMessageId = generateUniqueId();
        botMessageIdRef.current = botMessageId;

        setMessages(prev => prev.filter(msg => msg.id !== typingMsgId));
        setMessages(prev => [...prev, {
          id: botMessageId,
          type: 'text',
          sender: 'bot',
          text: '', // Raw text store hoga
          formattedText: '', // Formatted text ke liye alag field
          timestamp: new Date().toISOString()
        }]);

        // Streaming...
        const CHUNK_SIZE = 6;
        const DELAY = 8;
        let currentPosition = 0;
        let displayedText = '';

        const streamInterval = setInterval(() => {
          if (currentPosition < responseText.length) {
            const chunk = responseText.substr(currentPosition, CHUNK_SIZE);
            displayedText += chunk;
            currentPosition += CHUNK_SIZE;

            setMessages(prev =>
              prev.map(msg =>
                msg.id === botMessageId
                  ? { ...msg, text: displayedText } // Raw text store karo
                  : msg
              )
            );
            forceScrollToBottom();
          } else {
            clearInterval(streamInterval);
            typingIntervalRef.current = null;
            botMessageIdRef.current = null;
            setIsTyping(false);
            setIsProcessing(false);
            abortControllerRef.current = null;

            const finalMessage = {
              id: botMessageId,
              type: 'text',
              sender: 'bot',
              text: responseText, // Raw text store karo
              timestamp: new Date().toISOString()
            };

            updateChatHistory(userMessage, finalMessage, title);
            fetchChats();
            fetchUserCredits();
          }
        }, DELAY);

        typingIntervalRef.current = streamInterval;
        return;
      }

      // Handle normal text message
      response = await fetch('https://chatbot-ai-m3th.onrender.com/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          prompt: userMessageText,
          user_id: user_id || userId.current,
          chat_id: activeChat,
          title: title || 'New Chat',
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // Remove typing indicator
      setMessages(prev => prev.filter(msg => msg.id !== typingMsgId));

      if (data.type === "image") {
        const botMessage = {
          id: generateUniqueId(),
          type: 'image',
          sender: 'bot',
          text: data.description || "Generated image",
          image_url: data.image_url,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, botMessage]);
        updateChatHistory(userMessage, botMessage, title);
        fetchChats();

        setIsProcessing(false);
        setIsTyping(false);
        if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
        if (abortControllerRef.current) abortControllerRef.current = null;

        fetchUserCredits();
      } else {
        const responseText = data.content || data.response || JSON.stringify(data);
        const botMessageId = generateUniqueId();
        botMessageIdRef.current = botMessageId;

        setMessages(prev => [...prev, {
          id: botMessageId,
          type: 'text',
          sender: 'bot',
          text: '',
          timestamp: new Date().toISOString()
        }]);

        const CHUNK_SIZE = 6;
        const DELAY = 8;
        let currentPosition = 0;
        let displayedText = '';

        const streamInterval = setInterval(() => {
          if (currentPosition < responseText.length) {
            const chunk = responseText.substr(currentPosition, CHUNK_SIZE);
            displayedText += chunk;
            currentPosition += CHUNK_SIZE;

            setMessages(prev =>
              prev.map(msg =>
                msg.id === botMessageId
                  ? { ...msg, text: displayedText }
                  : msg
              )
            );
            forceScrollToBottom();
          } else {
            clearInterval(streamInterval);
            typingIntervalRef.current = null;
            botMessageIdRef.current = null;
            setIsTyping(false);
            setIsProcessing(false);
            abortControllerRef.current = null;

            const finalMessage = {
              id: botMessageId,
              type: 'text',
              sender: 'bot',
              text: responseText,
              timestamp: new Date().toISOString()
            };

            updateChatHistory(userMessage, finalMessage, title);
            fetchChats();
            fetchUserCredits();
          }
        }, DELAY);

        typingIntervalRef.current = streamInterval;
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        let errorMessage = 'Could not get response from server. Please try again.';

        setMessages(prev => [...prev.filter(msg => msg.id !== typingMsgId), {
          id: generateUniqueId(),
          type: 'text',
          sender: 'bot',
          text: 'Error: Could not get response from server. Please try again.',
          timestamp: new Date().toISOString()
        }]);

        showPopup(
          'Request Failed',
          errorMessage,
          'error',
          'center'
        );
      }
      setIsProcessing(false);
      setIsTyping(false);
    } finally {
      // Cleanup if something went wrong
      if (!botMessageIdRef.current) {
        setIsProcessing(false);
      }
    }
  };

  const formatMessageContent = (text, type = 'text') => {
    if (!text) return null;

    if (type === 'image') {
      return (
        <div className="generated-image-container">
          <img
            src={`https://chatbot-ai-m3th.onrender.com/${text}`}
            alt="Generated content"
            className="img-fluid rounded"
            style={{ maxHeight: '300px', maxWidth: '100%' }}
            onError={e => {
              e.target.onerror = null;
              e.target.src = '/placeholder-image.png';
            }}
          />
        </div>
      );
    }

    // Markdown inline formatting
    const processText = content => {
      if (!content) return '';
      let processed = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      processed = processed.replace(/\*(.+?)\*/g, '<em>$1</em>');
      processed = processed.replace(/`(.+?)`/g, '<code class="inline-code">$1</code>');
      return processed;
    };

    // Parse the content into blocks
    const blocks = [];
    const lines = text.split('\n');
    let inCode = false;
    let codeLang = '';
    let codeLines = [];
    let textLines = [];

    const pushTextBlock = () => {
      if (textLines.length > 0) {
        blocks.push({
          type: 'text',
          content: textLines.join('\n'),
        });
        textLines = [];
      }
    };

    lines.forEach((line) => {
      if (line.startsWith('```')) {
        if (!inCode) {
          // Starting a code block
          pushTextBlock();
          inCode = true;
          codeLang = line.replace('```', '').trim();
          codeLines = [];
        } else {
          // Ending a code block
          inCode = false;
          blocks.push({
            type: 'code',
            lang: codeLang,
            content: codeLines.join('\n'),
          });
          codeLang = '';
          codeLines = [];
        }
      } else if (inCode) {
        codeLines.push(line);
      } else {
        textLines.push(line);
      }
    });

    // Push any remaining blocks after loop
    pushTextBlock();
    if (codeLines.length > 0) {
      blocks.push({
        type: 'code',
        lang: codeLang,
        content: codeLines.join('\n'),
      });
    }

    // Render blocks with proper keys
    return (
      <div className="message-content">
        {blocks.map((block, index) => {
          if (block.type === 'code') {
            return (
              <div key={`code-${index}`} className="code-block-container position-relative mb-3">
                <div className="code-block bg-dark text-light p-3 rounded">
                  {block.lang && (
                    <div className="text-muted mb-2 font-monospace small">{block.lang}</div>
                  )}
                  <pre className="m-0">
                    <code className={`language-${block.lang || 'plaintext'}`}>
                      {block.content}
                    </code>
                  </pre>
                </div>
                <div className="code-actions position-absolute top-0 end-0 p-2 d-flex gap-1">
                  <button
                    className="btn btn-sm buttonn1"
                    onClick={() => handleCopyCode(block.content, block.id)}
                    title="Copy code"
                  >
                    <i className={copiedCodeId === block.id ? "bi bi-clipboard-check" : "bi bi-copy"}></i>
                    <span>
                      {copiedCodeId === block.id ? "  Copied" : "  Copy"}
                    </span>
                  </button>
                  <button
                    className="btn btn-sm buttonn1"
                    onClick={() => handleDownloadCode(block.content, block.lang, block.id)}
                    title="Download code"
                  >
                    <i className={downloadedCodeId === block.id ? "bi bi-check-square" : "bi bi-download"}></i>
                    <span>
                      {downloadedCodeId === block.id ? "  Downloaded" : "  Download"}
                    </span>
                  </button>
                </div>
              </div>
            );
          }

          // Process text blocks
          const paragraphs = block.content.split(/\n\s*\n/);
          return paragraphs
            .filter(p => p.trim())
            .map((paragraph, pIdx) => {
              const p = paragraph.trim();

              // Headings
              if (p.startsWith('### ')) {
                return (
                  <h1
                    key={`h1-${index}-${pIdx}`}
                    className="message-heading"
                    dangerouslySetInnerHTML={{ __html: processText(p.replace(/^### /, '')) }}
                  />
                );
              }
              if (p.startsWith('## ')) {
                return (
                  <h3
                    key={`h3-${index}-${pIdx}`}
                    className="message-heading"
                    dangerouslySetInnerHTML={{ __html: processText(p.replace(/^## /, '')) }}
                  />
                );
              }
              if (p.startsWith('# ')) {
                return (
                  <h3
                    key={`h3-${index}-${pIdx}`}
                    className="message-heading"
                    dangerouslySetInnerHTML={{ __html: processText(p.replace(/^# /, '')) }}
                  />
                );
              }

              // Lists
              if (p.startsWith('- ') || p.startsWith('* ')) {
                const items = p.split('\n');
                return (
                  <ul key={`ul-${index}-${pIdx}`} className="message-list">
                    {items.map((item, i) => (
                      <li
                        key={`li-${i}`}
                        dangerouslySetInnerHTML={{
                          __html: processText(item.replace(/^[-*] /, ''))
                        }}
                      />
                    ))}
                  </ul>
                );
              }

              if (/^\d+\.\s/.test(p)) {
                const items = p.split('\n');
                return (
                  <ol key={`ol-${index}-${pIdx}`} className="message-list">
                    {items.map((item, i) => (
                      <li
                        key={`li-${i}`}
                        dangerouslySetInnerHTML={{
                          __html: processText(item.replace(/^\d+\.\s/, ''))
                        }}
                      />
                    ))}
                  </ol>
                );
              }

              // Normal paragraph
              return (
                <p
                  key={`p-${index}-${pIdx}`}
                  className="message-paragraph"
                  dangerouslySetInnerHTML={{ __html: processText(p) }}
                />
              );
            });
        })}
      </div>
    );
  };

  const highlightSearchMatch = (text, query) => {
    if (!query) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ?
        <span key={i} className="text-danger fw-bold">{part}</span> :
        part
    );
  };

  const filteredChats = (chatList) => {
    if (!searchQuery) {
      return chatList.map(chat => ({
        ...chat,
        highlightedMessages: [], // Add empty array if no search
      }));
    }

    return chatList.filter(chat => {
      const titleMatch = chat.title?.toLowerCase().includes(searchQuery.toLowerCase());

      const messagesMatch = chat.messages?.some(msg => {
        return (
          (msg.prompt?.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (msg.response?.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      });

      return titleMatch || messagesMatch;
    }).map(chat => ({
      ...chat,
      highlightedTitle: highlightSearchMatch(chat.title || "", searchQuery),
      highlightedMessages: chat.messages?.map(msg => ({
        ...msg,
        highlightedPrompt: msg.prompt ? highlightSearchMatch(msg.prompt, searchQuery) : "",
        highlightedResponse: msg.response ? highlightSearchMatch(msg.response, searchQuery) : ""
      })) || []
    }));
  };

  const renderMessageContent = (msg) => {
    const messageText = msg.text || '';
    const BACKEND_URL = 'https://chatbot-ai-m3th.onrender.com';

    switch (msg.type) {
      case 'typing':
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="spinner-grow spinner-grow-sm text-white" role="status" />
          </div>
        );

      case 'image':
        return (
          <div className="message-content">
            <div className="bot-message">
              <img
                src={`data:image/png;base64,${msg.image_base64}`}
                alt={messageText}
                className="img-fluid rounded"
                style={{ maxHeight: '300px' }}
                onError={(e) => {
                  e.target.onerror = null;
                  if (msg.image_id) {
                    e.target.src = `${BACKEND_URL}/get-image/${msg.image_id}`;
                  }
                }}
              />
              <p className="mt-2 mb-0">
                {messageText}
                {msg.api_source === 'freepik' && (
                  <small className="text-muted ms-2">(Generated with FreePik AI)</small>
                )}
              </p>
            </div>
          </div>
        );

      case 'image-upload':
        return (
          <div className="message-content d-flex flex-column gap-2">
            {/* 🔥 Show loading skeleton if images are still loading */}
            {msg._rawImageUrls && msg._rawImageUrls.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {msg._rawImageUrls.map((_, idx) => (
                  <div
                    key={`loading-${idx}`}
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '8px',
                      backgroundColor: '#2d2d2d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <div className="spinner-border spinner-border-sm text-secondary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (Array.isArray(msg.image_urls) && msg.image_urls.length > 0) ||
              (Array.isArray(msg.images) && msg.images.length > 0) ? (
              <div className="d-flex flex-wrap gap-2">
                {(msg.image_urls || msg.images || []).map((url, idx) => {
                  if (!url || typeof url !== 'string') return null;

                  let fullImageUrl;
                  if (url.startsWith('blob:')) {
                    fullImageUrl = url;
                  } else if (url.startsWith('http://') || url.startsWith('https://')) {
                    fullImageUrl = url;
                  } else if (url.startsWith('/')) {
                    fullImageUrl = `${BACKEND_URL}${url}`;
                  } else {
                    fullImageUrl = `${BACKEND_URL}/${url}`;
                  }

                  return (
                    <div
                      key={`uploaded-img-${idx}-${msg.id || idx}`}
                      style={{
                        width: '100px',
                        height: '100px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: 'none',
                        outline: 'none',
                        backgroundColor: '#2d2d2d'
                      }}
                    >
                      <img
                        src={fullImageUrl}
                        alt={`Uploaded ${idx + 1}`}
                        className="img-fluid w-100 h-100"
                        style={{
                          objectFit: 'cover',
                          border: 'none',
                        }}
                        loading="eager"
                        onError={(e) => {
                          console.error(`Failed to load image:`, fullImageUrl);
                          if (!e.target.dataset.retried) {
                            e.target.dataset.retried = 'true';
                            setTimeout(() => {
                              e.target.src = fullImageUrl;
                            }, 1000);
                          } else {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\' viewBox=\'0 0 100 100\'%3E%3Crect width=\'100\' height=\'100\' fill=\'%23f1f5f9\'/%3E%3Ctext x=\'50%25\' y=\'50%25\' dominant-baseline=\'middle\' text-anchor=\'middle\' fill=\'%2394a3b8\' font-size=\'14\'%3E🖼️%3C/text%3E%3C/svg%3E';
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
            {msg.text && (
              <p className="m-0" style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                {msg.text}
              </p>
            )}
          </div>
        );

      default:
        return (
          <div className="message-content">
            {msg.sender === 'bot' ? (
              // 🔥 FIX: Hamesha formatMessageContent use karo bot messages ke liye
              formatMessageContent(msg.text, msg.type)
            ) : (
              msg.text.split('\n').map((line, i) => (
                <p key={`line-${i}`} className="mb-0">{line}</p>
              ))
            )}
          </div>
        );
    }
  };

  const renderMessages = () => (
    <main
      className="flex-grow-1 overflow-auto p-3 bg-color1"
      ref={messagesContainerRef}
      style={{
        height: '100%',
        overflowY: 'auto',
        // Mobile specific smooth scrolling
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <div className="d-flex flex-column gap-3">
        {messages.length === 0 ? (
          <div className="text-center mt-5">
            <div className="mb-4">
              <i className="bi bi-chat-square-text bg-color2 fs-1"></i>
            </div>
            <h5 className="bg-color2">Start a new conversation</h5>
            <p className="bg-color2">
              {selectedImages.length > 0 ?
                "You've selected images - add a message or send as is" :
                "Type a message below to begin chatting"}
            </p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id || msg.message_id || `msg-${Date.now()}-${Math.random()}`}
              className={`d-flex flex-column ${msg.sender === 'user' ? 'align-items-end' : 'align-items-start'}`}
            >
              <div
                className={`message-container p-3 rounded-3 ${msg.sender === 'user' ?
                  'user-message-container' :
                  msg.type === 'typing' ?
                    'shadow' :
                    'text-white shadow-sm shadow'
                  }`}
                style={{ maxWidth: '85%' }}
              >
                {renderMessageContent(msg)}

                <div className="text-start mt-1">
                  <small className={`text-opacity-75 ${msg.sender === 'user' ? 'time' : ''}`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </small>
                </div>
              </div>

              {msg.sender === 'bot' && msg.type !== 'typing' && !isProcessing && !isTyping && (
                <div className="d-flex justify-content-start gap-2 mt-1 ms-3" style={{ maxWidth: '75%' }}>
                  <button
                    className="btn btn-sm buttonn"
                    onClick={() => handleCopy(msg.text, msg.id)}
                    title="Copy message"
                  >
                    <i className={copiedMessageId === msg.id ? "bi bi-check-lg" : "bi bi-copy"}></i>
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      <div ref={bottomRef} />
    </main>
  );

  const renderInputArea = () => {
    const isAtLimit = messages.filter(m => m.type !== 'typing').length >= 40;

    return (
      <footer
        className="bg-color1 py-3 d-flex justify-content-center position-relative flex-shrink-0"
        style={{
          borderTop: 'none',
        }}
      >
        {messages.length > 3 && !shouldAutoScroll && (
          <button
            onClick={() => { setShouldAutoScroll(true); scrollToBottom(true); }}
            className="btn btn-sm color-6 floating"
            title="Scroll to bottom"
          >
            <i className="bi bi-chevron-down fs-5"></i>
          </button>
        )}

        {user_id && userCredits.remaining <= 5 && userCredits.remaining > 0 && (
          <div className="position-absolute top-0 start-50 translate-middle-x mt-1">
            <div className="badge bg-warning text-dark">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {userCredits.remaining} chat{userCredits.remaining !== 1 ? 's' : ''} remaining today
            </div>
          </div>
        )}

        <form
          onSubmit={handleSendMessage}
          style={{ width: '100%', maxWidth: '760px', border: 'none' }}
        >
          {/* Image previews above input */}
          <div className="d-flex overflow-auto pb-2" style={{ gap: '8px' }}>
            {selectedImages.map((image) => (
              <div key={image.id} className="position-relative flex-shrink-0">
                <img
                  src={image.preview}
                  alt={`Preview ${image.id}`}
                  style={{
                    height: '50px',
                    width: '50px',
                    objectFit: 'cover',
                    borderRadius: '12px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeImage(image.id)}
                  aria-label="Remove image"
                  className="btn btn-sm btn-danger p-0 d-flex align-items-center justify-content-center"
                  style={{
                    position: 'absolute',
                    top: '-1px',
                    right: '-8px',
                    width: '15px',
                    height: '15px',
                    borderRadius: '50%'
                  }}
                >
                  <i className="bi bi-x" style={{ fontSize: '0.7rem' }}></i>
                </button>
              </div>
            ))}
          </div>

          {/* Main input pill */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '8px',
              backgroundColor: theme === 'Light' ? '#f0f0f0' : 'rgb(60, 58, 58)',
              borderRadius: '28px',
              padding: '8px 10px 8px 16px',
              border: theme === 'Light'
                ? '1px solid rgba(0,0,0,0.1)'
                : '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {/* + / Image upload button */}
            <div className="flex-shrink-0" style={{ marginBottom: '2px' }}>
              <input
                type="file"
                id="image-upload"
                ref={fileInputRef}
                accept="image/*"
                className="d-none"
                onChange={handleImageUpload}
                multiple
                disabled={selectedImages.length >= MAX_IMAGES}
              />
              <label
                htmlFor="image-upload"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  backgroundColor: theme === 'Light'
                    ? 'rgba(0,0,0,0.08)'
                    : 'rgba(255,255,255,0.1)',
                  cursor: selectedImages.length >= MAX_IMAGES ? 'not-allowed' : 'pointer',
                  opacity: selectedImages.length >= MAX_IMAGES ? 0.5 : 1,
                  border: 'none',
                  margin: 0,
                  flexShrink: 0,
                }}
                title={selectedImages.length >= MAX_IMAGES ? `Max ${MAX_IMAGES} images` : 'Add image'}
              >
                {selectedImages.length > 0 ? (
                  <span style={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: theme === 'Light' ? '#333' : '#fff'
                  }}>
                    {selectedImages.length}/{MAX_IMAGES}
                  </span>
                ) : (
                  <i
                    className="bi bi-plus-lg"
                    style={{
                      fontSize: '1.1rem',
                      color: theme === 'Light' ? '#333' : '#ccc'
                    }}
                  ></i>
                )}
              </label>
            </div>

            {/* Textarea */}
            <textarea
              rows={1}
              className="flex-grow-1"
              value={inputValue}
              readOnly={isProcessing || isTyping || isUploadingImages || isAtLimit}
              onChange={(e) => {
                if (isProcessing || isTyping || isUploadingImages || isAtLimit) return;
                setInputValue(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 130)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  // Mobile pe Enter se send mat karo - new line aaye
                  const isMobile = window.innerWidth < 768 ||
                    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

                  if (isMobile) {
                    // Mobile: Enter = new line, kuch mat karo (default behavior)
                    return;
                  }

                  // Desktop: Enter = send
                  e.preventDefault();
                  if (!isProcessing && !isTyping && !isAtLimit) {
                    handleSendMessage(e);
                  }
                }
              }}
              placeholder={
                isAtLimit
                  ? 'Chat limit reached - Start a new chat'
                  : selectedImages.length > 0
                    ? 'Describe the image...'
                    : 'Message Marshall AI...'
              }
              ref={inputRef}
              style={{
                resize: 'none',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent',
                color: theme === 'Light' ? '#1e293b' : '#ffffff',
                fontSize: '16px',
                lineHeight: '1.45',
                minHeight: '34px',
                maxHeight: '130px',
                overflowY: 'auto',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                padding: '6px 0',
                scrollbarWidth: 'thin',
                scrollbarColor: theme === 'Dark'
                  ? '#555 transparent'
                  : '#cbd5e1 transparent',
              }}
            />

            {/* Character counter */}
            {inputValue.length > 7500 && (
              <div
                className="flex-shrink-0"
                style={{
                  fontSize: '0.6rem',
                  color: inputValue.length > 10000 ? '#ef5350' : '#ffa726',
                  marginBottom: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                {inputValue.length}/10k
              </div>
            )}

            {/* Send / Stop / New Chat button */}
            <div className="flex-shrink-0" style={{ marginBottom: '2px' }}>
              {isAtLimit ? (
                <button
                  type="button"
                  onClick={startNewChat}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    height: '34px',
                    padding: '0 12px',
                    borderRadius: '20px',
                    backgroundColor: '#323333',
                    border: 'none',
                    color: '#e8e6e6',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                  title="Start new chat"
                >
                  <i className="bi bi-plus-circle" style={{ fontSize: '0.9rem' }}></i>
                  New Chat
                </button>
              ) : (
                <button
                  type={isProcessing || isTyping || isUploadingImages ? 'button' : 'submit'}
                  onClick={(e) => {
                    if (isProcessing || isTyping || isUploadingImages) {
                      e.preventDefault();
                      cancelRequest();
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '34px',
                    height: '34px',
                    borderRadius: '50%',
                    backgroundColor:
                      isProcessing || isTyping || isUploadingImages
                        ? '#555'
                        : inputValue.trim() || selectedImages.length > 0
                          ? '#ffffff'
                          : theme === 'Light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    flexShrink: 0,
                    transition: 'background-color 0.15s ease',
                  }}
                  className='mobile-view'
                  title={
                    isProcessing || isTyping || isUploadingImages
                      ? 'Stop generating'
                      : 'Send message'
                  }
                >
                  {isProcessing || isTyping || isUploadingImages ? (
                    <i
                      className="bi bi-stop-fill"
                      style={{ fontSize: '1rem', color: '#fff' }}
                    ></i>
                  ) : (
                    <i
                      className="bi bi-arrow-up-short"
                      style={{
                        fontSize: '1.5rem',
                        color:
                          inputValue.trim() || selectedImages.length > 0
                            ? '#1a1a1a'
                            : theme === 'Light' ? '#888' : '#666',
                        fontWeight: 'bold',
                      }}
                    ></i>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Bottom hint */}
          <div className="text-center" style={{ marginTop: '4px' }}>
            <small
              style={{
                fontSize: '0.65rem',
                opacity: 0.45,
                color: theme === 'Light' ? '#475569' : '#ffffff',
              }}
            >
              {isAtLimit
                ? '⚠️ 40 message limit reached'
                : isProcessing || isTyping
                  ? 'Tap stop to cancel'
                  : 'AI-Generated and can make mistakes'}
            </small>
          </div>
        </form>
      </footer>
    );
  };

  // Add this component before the Home function
  const LoadingSkeleton = () => {
    return (
      <div className="d-flex vh-100" style={{
        height: '100dvh',
        overflow: 'hidden',
        backgroundColor: theme === 'Dark' ? '#1a1a1a' : '#ffffff'
      }}>
        {/* Sidebar Skeleton */}
        <div className="sidebar shadow-sm d-flex flex-column visible" style={{ width: '260px' }}>
          <div className="sticky-top">
            <div className="d-flex justify-content-between align-items-center px-3 pt-3">
              <div className="skeleton skeleton-text" style={{ width: '120px', height: '24px' }}></div>
              <div className="skeleton" style={{ width: '32px', height: '32px', borderRadius: '8px' }}></div>
            </div>

            <div className="d-flex px-2 pt-3">
              <div className="skeleton w-100" style={{ height: '40px', borderRadius: '8px' }}></div>
            </div>

            <div className="p-2">
              <div className="skeleton w-100" style={{ height: '40px', borderRadius: '8px' }}></div>
            </div>

            <div className="ms-3 mb-2">
              <div className="skeleton" style={{ width: '100px', height: '16px', borderRadius: '4px' }}></div>
            </div>
          </div>

          <div className="flex-grow-1 overflow-auto px-3 pt-2">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="mb-3 p-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="skeleton mb-2" style={{ width: `${70 + Math.random() * 20}%`, height: '16px', borderRadius: '4px' }}></div>
                    <div className="skeleton" style={{ width: `${40 + Math.random() * 20}%`, height: '12px', borderRadius: '4px' }}></div>
                  </div>
                  <div className="skeleton" style={{ width: '24px', height: '24px', borderRadius: '4px' }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Skeleton */}
        <div className="flex-grow-1 d-flex flex-column" style={{ marginLeft: '260px' }}>
          <header className="p-4 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="skeleton me-2" style={{ width: '40px', height: '40px', borderRadius: '8px' }}></div>
              <div className="skeleton" style={{ width: '200px', height: '28px', borderRadius: '4px' }}></div>
            </div>
            <div className="skeleton" style={{ width: '40px', height: '40px', borderRadius: '50%' }}></div>
          </header>

          <main className="flex-grow-1 p-4">
            <div className="text-center mt-5">
              <div className="skeleton mb-4 mx-auto" style={{ width: '80px', height: '80px', borderRadius: '50%' }}></div>
              <div className="skeleton mx-auto mb-3" style={{ width: '250px', height: '24px', borderRadius: '4px' }}></div>
              <div className="skeleton mx-auto" style={{ width: '350px', height: '16px', borderRadius: '4px' }}></div>
            </div>
          </main>

          <footer className="p-4 d-flex justify-content-center flex-shrink-0" style={{
            backgroundColor: 'transparent',
            borderTop: 'none'
          }}>
            <div className="skeleton" style={{
              width: '100%',
              maxWidth: '760px',
              height: '70px',
              borderRadius: '30px',
              backgroundColor: '#2d2d2d' // Dark theme ke liye
            }}></div>
          </footer>
        </div>
      </div>
    );
  };

  // Replace the early returns at the bottom with this:
  if (!user_id) {
    return <LoggedOutHome />;
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <i className="bi bi-exclamation-triangle text-danger fs-1 mb-3"></i>
          <p className="text-danger">{error}</p>
          <button
            className="btn btn-primary mt-2"
            onClick={() => window.location.reload()}
          >
            <i className="bi bi-arrow-repeat me-2"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex vh-100" style={{ height: '100dvh', overflow: 'hidden' }}>
      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show' : ''}`}
        style={{ display: showDeleteModal ? 'block' : 'none' }}
        tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this chat? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDelete}>
                <i className="bi bi-trash me-1"></i> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && <div className="modal-backdrop fade show"></div>}

      <div
        className={`modal fade ${showLogoutModal ? 'show' : ''}`}
        style={{
          display: showLogoutModal ? 'block' : 'none',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
        tabIndex="-1"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowLogoutModal(false);
          }
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content bg-color3 rounded-4" style={{
            border: 'none',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)'
          }}>
            <div className="modal-header border-0 px-4 pt-4">
              <h5 className="modal-title text-white">
                <i className="bi bi-box-arrow-right text-warning me-2"></i>
                Confirm Logout
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={cancelLogout}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body px-4 py-3">
              <p className="text-white mb-0">
                Are you sure you want to log out?
              </p>
              <small className="text-muted">
                You will need to sign in again to access your account.
              </small>
            </div>

            <div className="modal-footer border-0 px-4 pb-4">
              <button
                type="button"
                className="btn btn-outline-light cancel-btn"
                onClick={cancelLogout}
              >
                <i className="bi bi-x-circle me-1"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmLogout}
              >
                <i className="bi bi-box-arrow-right me-1"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
      {showLogoutModal && <div className="modal-backdrop fade show"></div>}

      {/* Search Modal */}
      <div className={`modal fade ${showSearchModal ? 'show' : ''}`}
        style={{
          display: showSearchModal ? 'block' : 'none',
          background: 'rgba(26, 25, 25, 0.6)'
        }}
        tabIndex="-1"
        onClick={(e) => {
          // Close modal when clicking outside the modal content
          if (e.target === e.currentTarget) {
            setShowSearchModal(false);
          }
        }}
      >
        <div className="modal-dialog modal-dialog-centered justify-content-between align-items-center" style={{ maxWidth: '650px' }}>
          <div className="modal-content bg-color3 rounded-3 pb-3" style={{
            border: 'none',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="modal-header border-0">
              <div className="w-100">
                <div className="input-group">
                  <span className="input-group-text bg-transparent border-0">
                    <i className="bi bi-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-0 shadow-none"
                    placeholder="Search chats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                    ref={searchInputRef}
                    style={{
                      fontSize: '1rem',
                      padding: '0.75rem 0',
                      backgroundColor: 'transparent'
                    }}
                  />
                  <button
                    className="input-group-text bg-transparent border-0"
                    type="button"
                    onClick={() => setShowSearchModal(false)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="bi bi-x-lg bg-change"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-body p-0">
              <div
                className="search-results"
                style={{
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  padding: '0 1rem',
                }}
              >
                {filteredChats(chats).length === 0 ? (
                  <div className="text-muted text-center py-4">
                    <i className="bi bi-search fs-4 mb-2 d-block"></i>
                    No chats found
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filteredChats(chats).map((chat) => (
                      <div
                        key={chat.chat_id}
                        className={`list-group-item list-group-item-action px-3 py-3 border-0 rounded-3 mb-1 ${activeChat === chat.chat_id ? 'active' : ''
                          }`}
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                        }}
                        onClick={() => {
                          loadChat(chat);
                          setShowSearchModal(false);
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <strong className="text-truncate">
                            {chat.highlightedTitle || chat.title}
                          </strong>
                          <small className="text-muted ms-2">
                            {formatDate(chat.updated_at || chat.created_at)}
                          </small>
                        </div>

                        {/* Message preview */}
                        {(chat.highlightedMessages || []).slice(0, 1).map((msg, idx) => (
                          <div
                            key={idx}
                            className="text-muted small mt-2"
                            style={{
                              maxHeight: '3rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {msg.prompt && (
                              <div>
                                <span className="fw-semibold">You:</span>{' '}
                                {msg.highlightedPrompt || msg.prompt}
                              </div>
                            )}
                            {msg.response && (
                              <div>
                                <span className="fw-semibold">Assistant:</span>{' '}
                                {msg.highlightedResponse || msg.response}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSearchModal && <div className="modal-backdrop fade show"></div>}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`sidebar shadow-sm d-flex flex-column ${isSidebarVisible ? 'visible' : 'hidden'}`}
      >
        {/* --- Sticky Header Section --- */}
        <div className="sticky-top z-3">
          {/* Header with Website Name and Close Button */}
          <div className="d-flex justify-content-between align-items-center px-3 pt-3">
            {/* Website Name on LEFT side */}
            <div className="fw-bold fs-6 text-white d-flex align-items-center">
              <span className="website-name" style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                background: 'linear-gradient(45deg, #4cc9f0, #4361ee)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '0.5px'
              }}>
                Marshall AI
              </span>
            </div>

            {isSidebarVisible && (
              <button
                className="btn color-6 btn-sm"
                onClick={toggleSidebar}
                aria-label="Close sidebar"
                style={{ lineHeight: '1' }}
                title="close sidebar"
              >
                <i className="bi bi-layout-sidebar-inset-reverse"></i>
              </button>
            )}
          </div>

          {/* New Chat Button */}
          <div className="d-flex px-2 pt-3">
            <button
              onClick={startNewChat}
              className="btn btn-outline-secondary border-0 text-start d-flex gap-2 w-100 new-chat-btn"
              disabled={isTyping || isProcessing}
            >
              <i className="fa-regular fa-pen-to-square pt-1"></i>
              <span className="text-truncate">New Chat</span>
            </button>
          </div>

          {/* Search Button */}
          <div className="p-2">
            <div className="input-group">
              <button
                className="btn btn-outline-secondary w-100 border-0 text-start d-flex new-chat-btn"
                onClick={() => setShowSearchModal(true)}
              >
                <i className="bi bi-search me-2"></i>
                <span className="text-truncate">Search chats</span>
              </button>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-0 ms-3">
            <h6 className="text-muted mb-2">Recent chats</h6>
          </div>
        </div>

        {/* --- Scrollable Chat List Section --- */}
        <div className="flex-grow-1 overflow-auto px-3 pt-2">

          <div className="list-group list-group-flush chat-list" style={{ paddingBottom: '1rem' }}>
            {chats.length === 0 ? (
              <div className="text-muted p-3 text-center">
                <i className="bi bi-chat-square-text fs-4 mb-2 d-block"></i>
                No active chats found
              </div>
            ) : (
              chats
                .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
                .map(chat => (
                  <div
                    key={chat.chat_id}
                    className={`list-group-item list-group-item-action border-0 px-3 py-2 ${activeChat === chat.chat_id ? 'active' : ''}`}
                    onClick={() => {
                      // Sirf tab loadChat karo jab editing mode mein na ho
                      if (editingChatId) return;
                      loadChat(chat);
                      setShowSearchModal(false);
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                        <div
                          style={{
                            maxWidth: '180px',
                            height: '1.5rem',
                            lineHeight: '1.5rem',
                            fontSize: '0.95rem',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onClick={(e) => {
                            // Editing mode mein parent click prevent karo
                            if (editingChatId === chat.chat_id) {
                              e.stopPropagation();
                            }
                          }}
                        >
                          {editingChatId === chat.chat_id && renameSource === 'sidebar' ? (
                            <input
                              ref={renameInputRef}
                              type="text"
                              className="form-control form-control-sm fw-semibold border-0 p-0 m-0 text-white"
                              style={{
                                height: '1.5rem',
                                lineHeight: '1.5rem',
                                fontSize: '0.95rem',
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                              }}
                              value={tempTitle}
                              onChange={(e) => setTempTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.stopPropagation();
                                  handleRenameInline(chat.chat_id);
                                }
                                if (e.key === 'Escape') {
                                  e.stopPropagation();
                                  setEditingChatId(null);
                                  setTempTitle('');
                                }
                              }}
                              onBlur={() => handleRenameInline(chat.chat_id)}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                            <span
                              className="text-truncate text-start fw-semibold"
                              style={{
                                maxWidth: '100%',
                                cursor: 'pointer',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {chat.highlightedTitle || chat.title}
                            </span>
                          )}
                          <small className="text-muted" style={{
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            marginLeft: 'auto',
                          }}>
                            {formatDate(chat.updated_at || chat.created_at)}
                          </small>
                        </div>
                      </div>

                      {!editingChatId && (
                        <div className="dropdown">
                          <button
                            className="btn btn-sm border-0 p-0 d-flex align-items-center justify-content-center chat-menu-trigger"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            style={{ width: '24px', height: '24px' }}
                            onClick={(e) => {
                              e.stopPropagation();  // ← Important: parent click prevent
                            }}
                          >
                            <div className="d-flex gap-1 color-7">
                              <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px' }}></span>
                              <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px' }}></span>
                              <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px' }}></span>
                            </div>
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end shadow-sm" onClick={(e) => e.stopPropagation()}>
                            <li>
                              <button
                                className="dropdown-item text-white rename-chat-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();  // ← Important: parent click prevent
                                  setEditingChatId(chat.chat_id);
                                  setTempTitle(chat.title);
                                  setRenameSource('sidebar');
                                  setTimeout(() => {
                                    renameInputRef.current?.focus();
                                  }, 50);  // Thoda delay for smooth rendering
                                }}
                              >
                                <i className="bi bi-pencil me-2"></i>Rename
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item text-white rename-chat-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();  // ← Important: parent click prevent
                                  handleShare(chat.chat_id);
                                }}
                              >
                                <i className="bi bi-share me-2"></i>Share
                              </button>
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                              <button
                                className="dropdown-item text-danger delete-chat-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();  // ← Important: parent click prevent
                                  confirmDelete(chat.chat_id);
                                }}
                              >
                                <i className="bi bi-trash me-2"></i>Delete
                              </button>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-grow-1 d-flex flex-column ${isSidebarVisible ? 'ms-sidebar' : 'ms-collapsed-sidebar'}`}
        style={{ height: '100dvh', overflow: 'hidden' }}
      >
        {/* Header */}
        <header className="bg-color p-4 d-flex align-items-center justify-content-between sticky-top flex-shrink-0">
          <div className="d-flex align-items-center">
            {/* Mobile Menu Button - Only shows on mobile */}
            <button
              className="mobile-menu-button d-md-none me-2"
              onClick={toggleSidebar}
              aria-label="Open sidebar"
            >
              <i className="bi bi-list"></i>
            </button>

            {/* Credits - DESKTOP ONLY (mobile pe hide) */}
            {user_id && (
              <div className="d-none d-md-flex align-items-center gap-2 ms-3">
                <div className="badge bg-transparent d-flex align-items-center gap-1" style={{ minWidth: '80px' }}>
                  {creditsLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </span>
                  ) : (
                    <>
                      <i className="bi bi-lightning-charge"></i>
                      <span>{userCredits.remaining}/{userCredits.total}</span>
                    </>
                  )}
                </div>
                <small className="text-muted d-none d-lg-block">Daily Credits</small>
              </div>
            )}
          </div>

          {!isSidebarVisible && (
            <div className="collapsed-sidebar d-flex flex-column align-items-center py-3 ">
              <button
                className="btn btn-sm color-5 "
                onClick={toggleSidebar}
                aria-label="Open sidebar"
                title='open sidebar'
              >
                <i className="bi bi-layout-sidebar-inset color-5"></i>
              </button>

              <button
                onClick={startNewChat}
                className="btn btn-outline-secondary border-0 mt-5 color-5"
                disabled={isTyping || isProcessing}
              >
                <i className="fa-regular fa-pen-to-square color-5"></i>
              </button>

              <button
                className="btn btn-outline-secondary border-0 mt-2 color-5"
                onClick={() => setShowSearchModal(true)}
              >
                <i className="bi bi-search color-5"></i>
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center align-items-center flex-grow-1">
            {editingChatId === activeChat && renameSource === 'header' ? (
              <input
                ref={renameInputRef}
                className="form-control text-center fw-semibold border-0 chat-title-input"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameInline(activeChat);
                  if (e.key === 'Escape') {
                    setEditingChatId(null);
                    setTempTitle('');
                  }
                }}
                onBlur={() => handleRenameInline(activeChat)}
                autoFocus
              />
            ) : (
              <div
                className="chat-title-header"
                onClick={() => {
                  const found = chats.find(c => c.chat_id === activeChat);
                  if (found) {
                    setEditingChatId(activeChat);
                    setTempTitle(found.title);
                    setRenameSource('header');
                    setTimeout(() => renameInputRef.current?.focus(), 0);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(13, 110, 253, 0.3)';
                  e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <h4 className="mb-0 fw-semibold text-truncate">
                  {chats.find(c => c.chat_id === activeChat)?.title || 'New Chat'}
                </h4>
              </div>
            )}

            {/* Profile Dropdown */}
            <div className="dropdown" style={{ position: 'absolute', right: 10 }}>
              <button
                className="btn hover-1 mt-2 d-flex align-items-center justify-content-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {UserImage?.picture_url ? (
                  <img
                    src={UserImage.picture_url}
                    alt="Profile"
                    className="rounded-circle"
                    style={{
                      width: '32px',
                      height: '32px',
                      objectFit: 'cover',
                    }}
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.onerror = null;
                      SetUserImage({ ...UserImage, picture_url: null });
                    }}
                  />
                ) : (
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                    style={{
                      width: '32px',
                      height: '32px',
                      fontSize: '1rem',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      textTransform: 'uppercase',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    {Userinfo?.username?.[0]?.toUpperCase() || Userinfo?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
              </button>

              <ul className="dropdown-menu dropdown-menu-end" style={{
                backgroundColor: '#2d2d2d',
                border: 'none',
                minWidth: '240px',
                borderRadius: '12px',
                padding: '8px 0',
                boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.4)'
              }}>
                {/* MOBILE CREDITS DISPLAY - Email ke upar */}
                {user_id && (
                  <>
                    <li className="px-3 py-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="d-flex align-items-center" style={{ fontSize: '0.9rem', color: '#e0e0e0' }}>
                          <i className="bi bi-lightning-charge me-2" style={{ color: '#fbbf24' }}></i>
                          <span className="credits-text">Daily Credits</span>
                        </span>
                        <span className="badge d-flex align-items-center" style={{
                          backgroundColor: '#3a3a3a',
                          color: 'white',
                          fontSize: '0.85rem',
                          padding: '0.35rem 0.65rem'
                        }}>
                          {creditsLoading ? (
                            <span className="spinner-border spinner-border-sm" style={{ width: '0.8rem', height: '0.8rem' }} role="status">
                              <span className="visually-hidden">Loading...</span>
                            </span>
                          ) : (
                            `${userCredits.remaining}/${userCredits.total}`
                          )}
                        </span>
                      </div>
                    </li>
                  </>
                )}

                {/* User Email */}
                <li className="px-3 py-2 text-muted" style={{ fontSize: '0.85rem' }}>
                  <i className="bi bi-person me-2" />
                  {Userinfo.email}
                </li>
                <li><hr className="dropdown-divider my-1" /></li>

                <li>
                  <button
                    className="dropdown-item d-flex align-items-center"
                    onClick={() => setShowHelpModal(true)}
                  >
                    <i className="bi bi-question-circle me-2" />
                    Help
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center"
                    onClick={() => setShowSettingsModal(true)}
                  >
                    <i className="bi bi-gear me-2"></i>
                    Settings
                  </button>
                </li>
                <li><hr className="dropdown-divider my-1" /></li>
                <li>
                  <button
                    className="dropdown-item text-danger d-flex align-items-center"
                    onClick={() => setShowLogoutModal(true)}  // ← YEH CHANGE KARO
                  >
                    <i className="fa-solid fa-arrow-right-from-bracket me-2"></i>
                    Log out
                  </button>
                </li>
              </ul>
            </div>

            {/* Customize Modal */}
            <div
              className={`modal fade ${showCustomizeModal ? 'show' : ''}`}
              style={{
                display: showCustomizeModal ? 'block' : 'none',
                background: 'rgba(26, 25, 25, 0.6)'
              }}
              tabIndex="-1"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowCustomizeModal(false);
                }
              }}
            >
              <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px' }}>
                <div className="modal-content bg-color3 rounded-3 pb-3" ref={customizeModalRef} style={{
                  border: 'none',
                  boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)'
                }}>
                  <div className="modal-header border-0">
                    <h5 className="modal-title text-light">
                      <i className="bi bi-sliders me-2"></i>
                      Customize Your Experience
                    </h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setShowCustomizeModal(false)}
                      aria-label="Close"
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label text-light">Theme</label>
                      <select
                        className="form-select bg-dark text-light border-secondary"
                        value={theme}
                        onChange={(e) => setTheme(e.target.value)}
                      >
                        <option>Dark</option>
                        <option>Light</option>
                        <option>System</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label text-light">Layout Density</label>
                      <select
                        className="form-select bg-dark text-light border-secondary"
                        value={layout}
                        onChange={(e) => setLayout(e.target.value)}
                      >
                        <option>Compact</option>
                        <option>Normal</option>
                        <option>Spacious</option>
                      </select>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="notificationsSwitch"
                        checked={notificationsEnabled}
                        onChange={(e) => setNotificationsEnabled(e.target.checked)}
                      />
                      <label className="form-check-label text-light" htmlFor="notificationsSwitch">
                        Enable Notifications
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer border-0">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCustomizeModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        // Save logic here
                        setShowCustomizeModal(false);
                      }}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {showCustomizeModal && <div className="modal-backdrop fade show"></div>}

            {/* Settings Modal */}
            <div
              className={`modal fade ${showSettingsModal ? 'show' : ''}`}
              style={{
                display: showSettingsModal ? 'block' : 'none',
                background: theme === 'Dark' ? 'rgba(26, 25, 25, 0.6)' : 'rgba(0, 0, 0, 0.3)'
              }}
              tabIndex="-1"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowSettingsModal(false);
                }
              }}
            >
              <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '620px' }}>
                <div
                  className="modal-content rounded-3"
                  ref={settingsModalRef}
                  style={{
                    border: theme === 'Dark' ? '0.5px solid rgba(255,255,255,0.1)' : '0.5px solid rgba(0,0,0,0.1)',
                    boxShadow: theme === 'Dark' ? '0 0 30px rgba(0,0,0,0.5)' : '0 0 30px rgba(0,0,0,0.15)',
                    backgroundColor: theme === 'Dark' ? '#1e1e1e' : '#ffffff',
                    overflow: 'hidden',
                    maxHeight: '95vh',
                    width: '100%',
                  }}
                >
                  {/* Modal Header - Fixed with proper close button positioning */}
                  <div
                    className="modal-header d-flex align-items-center justify-content-between"
                    style={{
                      borderBottom: theme === 'Dark' ? '0.5px solid rgba(255,255,255,0.1)' : '0.5px solid rgba(0,0,0,0.1)',
                      padding: '20px 24px 16px',
                      flexShrink: 0,
                    }}
                  >
                    <h5 className="modal-title" style={{
                      color: theme === 'Dark' ? '#fff' : '#1a1a1a',
                      fontWeight: 500,
                      fontSize: '17px'
                    }}>
                      Settings
                    </h5>
                    <button
                      type="button"
                      onClick={() => setShowSettingsModal(false)}
                      aria-label="Close"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: theme === 'Dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                        fontSize: '18px',
                        padding: 0,
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = theme === 'Dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
                        e.currentTarget.style.color = theme === 'Dark' ? '#fff' : '#000';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = theme === 'Dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Modal Body - FIXED HEIGHT */}
                  <div className="d-flex settings-modal-body" style={{
                    height: 'min(500px, calc(95vh - 65px))',
                    overflow: 'hidden',
                  }}>

                    {/* Sidebar Nav - Fixed (no scroll) */}
                    <nav className="settings-modal-nav" style={{
                      width: '170px',
                      flexShrink: 0,
                      borderRight: theme === 'Dark' ? '0.5px solid rgba(255,255,255,0.1)' : '0.5px solid rgba(0,0,0,0.1)',
                      padding: '12px 0',
                      height: '100%',
                    }}>
                      {[
                        {
                          id: 'general', label: 'General',
                          icon: (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                              <circle cx="12" cy="12" r="3" />
                              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                            </svg>
                          )
                        },
                        {
                          id: 'profile', label: 'Profile',
                          icon: (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          )
                        },
                        {
                          id: 'about', label: 'About',
                          icon: (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                          )
                        },
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveSettingsTab(tab.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '11px 20px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: activeSettingsTab === tab.id
                              ? (theme === 'Dark' ? '#fff' : '#1a1a1a')
                              : (theme === 'Dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'),
                            fontWeight: activeSettingsTab === tab.id ? 500 : 400,
                            background: activeSettingsTab === tab.id
                              ? (theme === 'Dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)')
                              : 'none',
                            border: 'none',
                            width: '100%',
                            textAlign: 'left',
                            transition: 'background 0.15s, color 0.15s',
                          }}
                          onMouseEnter={e => {
                            if (activeSettingsTab !== tab.id) {
                              e.currentTarget.style.background = theme === 'Dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                              e.currentTarget.style.color = theme === 'Dark' ? '#fff' : '#1a1a1a';
                            }
                          }}
                          onMouseLeave={e => {
                            if (activeSettingsTab !== tab.id) {
                              e.currentTarget.style.background = 'none';
                              e.currentTarget.style.color = theme === 'Dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
                            }
                          }}
                        >
                          <span style={{ opacity: activeSettingsTab === tab.id ? 1 : 0.7, display: 'flex' }}>
                            {tab.icon}
                          </span>
                          {tab.label}
                        </button>
                      ))}
                    </nav>

                    {/* Content Area - WITH SCROLL */}
                    <div style={{
                      flex: 1,
                      padding: '28px',
                      overflowY: 'auto',
                      height: '100%',
                    }}>
                      {/* Custom Scrollbar Styles */}
                      <style>
                        {`
              .settings-content::-webkit-scrollbar {
                width: 6px;
              }
              .settings-content::-webkit-scrollbar-track {
                background: transparent;
              }
              .settings-content::-webkit-scrollbar-thumb {
                background: ${theme === 'Dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'};
                border-radius: 3px;
              }
              .settings-content::-webkit-scrollbar-thumb:hover {
                background: ${theme === 'Dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'};
              }
            `}
                      </style>

                      {/* General Tab */}
                      {activeSettingsTab === 'general' && (
                        <div>
                          <p style={{
                            fontSize: '14px',
                            color: theme === 'Dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                            marginBottom: '14px'
                          }}>
                            Theme
                          </p>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            {[
                              {
                                id: 'Light',
                                icon: (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                    <circle cx="12" cy="12" r="5" />
                                    <line x1="12" y1="1" x2="12" y2="3" />
                                    <line x1="12" y1="21" x2="12" y2="23" />
                                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                                    <line x1="1" y1="12" x2="3" y2="12" />
                                    <line x1="21" y1="12" x2="23" y2="12" />
                                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                                  </svg>
                                )
                              },
                              {
                                id: 'Dark',
                                icon: (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                                  </svg>
                                )
                              },
                            ].map(t => (
                              <div
                                key={t.id}
                                onClick={() => {
                                  setTheme(t.id);
                                  localStorage.setItem('theme', t.id);
                                  document.body.classList.remove('theme-dark', 'theme-light');
                                  if (t.id === 'System') {
                                    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                                    document.body.classList.add(prefersDark ? 'theme-dark' : 'theme-light');
                                  } else {
                                    document.body.classList.add(`theme-${t.id.toLowerCase()}`);
                                  }
                                  setGeneralSettings(prev => ({ ...prev, darkMode: t.id === 'Dark' }));
                                }}
                                style={{
                                  flex: 1,
                                  border: theme === t.id
                                    ? (theme === 'Dark' ? '1.5px solid rgba(255,255,255,0.6)' : '1.5px solid rgba(0,0,0,0.6)')
                                    : (theme === 'Dark' ? '1.5px solid rgba(255,255,255,0.12)' : '1.5px solid rgba(0,0,0,0.12)'),
                                  borderRadius: '10px',
                                  padding: '18px 10px 14px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '8px',
                                  color: theme === t.id
                                    ? (theme === 'Dark' ? '#fff' : '#1a1a1a')
                                    : (theme === 'Dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'),
                                  fontSize: '13px',
                                  background: theme === t.id
                                    ? (theme === 'Dark' ? '#333' : '#f0f0f0')
                                    : (theme === 'Dark' ? '#2a2a2a' : '#fafafa'),
                                  transition: 'all 0.15s',
                                }}
                              >
                                {t.icon}
                                {t.id}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Profile Tab */}
                      {activeSettingsTab === 'profile' && (
                        <div className="settings-content">
                          {/* Avatar Row */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            paddingBottom: '20px',
                            borderBottom: theme === 'Dark' ? '0.5px solid rgba(255,255,255,0.08)' : '0.5px solid rgba(0,0,0,0.08)',
                            marginBottom: '4px',
                          }}>
                            {UserImage?.picture_url ? (
                              <img
                                src={UserImage.picture_url}
                                alt="Profile"
                                style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                onError={(e) => { e.target.onerror = null; SetUserImage({ ...UserImage, picture_url: null }); }}
                              />
                            ) : (
                              <div style={{
                                width: '56px', height: '56px', borderRadius: '50%',
                                background: theme === 'Dark' ? '#555' : '#e0e0e0',
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontSize: '20px', fontWeight: 500,
                                color: theme === 'Dark' ? '#fff' : '#555',
                                flexShrink: 0, textTransform: 'uppercase',
                              }}>
                                {Userinfo?.username?.[0]?.toUpperCase() || Userinfo?.email?.[0]?.toUpperCase() || 'U'}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <input
                                type="file"
                                ref={profilePicInputRef}
                                accept="image/jpeg,image/png,image/webp"
                                style={{ display: 'none' }}
                                onChange={handleProfilePicUpload}
                              />
                              <button
                                onClick={() => profilePicInputRef.current?.click()}
                                disabled={isUploadingProfile}
                                style={{
                                  background: 'none',
                                  border: theme === 'Dark' ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                  color: theme === 'Dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                  borderRadius: '8px',
                                  padding: '7px 14px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                }}
                                className='upload-button'
                              >
                                {isUploadingProfile ? 'Uploading...' : 'Upload new'}
                              </button>
                              {UserImage?.picture_url && (
                                <button
                                  onClick={handleRemoveProfilePic}
                                  style={{
                                    background: 'none',
                                    border: '1px solid rgba(220,80,80,0.35)',
                                    color: '#e57373',
                                    borderRadius: '8px',
                                    padding: '7px 14px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                  }}
                                  className='remove-btn'
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Info Rows */}
                          {[
                            { label: 'Name', value: Userinfo?.username || '—' },
                            { label: 'Email address', value: Userinfo?.email || '—' },
                            { label: 'Login type', value: Userinfo?.auth_type || '—' },
                            { label: 'Joined', value: Userinfo?.created_at ? formatDate(Userinfo.created_at) : '—' },
                          ].map((row, i) => (
                            <div key={i} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '15px 0',
                              borderBottom: theme === 'Dark' ? '0.5px solid rgba(255,255,255,0.08)' : '0.5px solid rgba(0,0,0,0.08)',
                            }}>
                              <span style={{
                                fontSize: '14px',
                                color: theme === 'Dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
                              }}>
                                {row.label}
                              </span>
                              <span style={{
                                fontSize: '13px',
                                color: theme === 'Dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                              }}>
                                {row.value}
                              </span>
                            </div>
                          ))}

                          {/* Log out row */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '15px 0',
                            borderBottom: theme === 'Dark' ? '0.5px solid rgba(255,255,255,0.08)' : '0.5px solid rgba(0,0,0,0.08)',
                          }}>
                            <span style={{
                              fontSize: '14px',
                              color: theme === 'Dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
                            }}>
                              Log out of all devices
                            </span>
                            <button
                              onClick={() => { setShowSettingsModal(false); setShowLogoutModal(true); }}
                              style={{
                                background: 'none',
                                border: '1px solid rgba(220,80,80,0.35)',
                                color: '#e57373',
                                borderRadius: '20px',
                                padding: '7px 18px',
                                fontSize: '13px',
                                cursor: 'pointer',
                              }}
                              className='remove-btn'
                            >
                              Log out
                            </button>
                          </div>

                          {/* Delete Account row */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '15px 0',
                          }}
                          >
                            <span style={{
                              fontSize: '14px',
                              color: theme === 'Dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
                            }}>
                              Delete account
                            </span>
                            <button
                              onClick={() => showPopup('Coming Soon', 'Account deletion will be available soon.', 'info', 'center')}
                              style={{
                                background: 'none',
                                border: '1px solid rgba(200,60,60,0.35)',
                                color: '#ef5350',
                                borderRadius: '20px',
                                padding: '7px 18px',
                                fontSize: '13px',
                                cursor: 'pointer',
                              }}
                              className='remove-btn'
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}

                      {/* About Tab */}
                      {activeSettingsTab === 'about' && (
                        <div className="settings-content">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                            <div style={{
                              width: '44px', height: '44px', borderRadius: '12px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <img src={logo} alt="Logo" style={{ width: '36px', height: '36px' }} />
                            </div>
                            <div>
                              <div style={{
                                fontSize: '17px',
                                fontWeight: 500,
                                color: theme === 'Dark' ? '#fff' : '#1a1a1a'
                              }}>
                                Marshall AI
                              </div>
                              <div style={{
                                fontSize: '13px',
                                color: theme === 'Dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                marginTop: '2px'
                              }}>
                                Version 1.0.1
                              </div>
                            </div>
                          </div>

                          <p style={{
                            fontSize: '13px',
                            color: theme === 'Dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                            lineHeight: 1.6,
                            marginBottom: '20px'
                          }}>
                            A modern AI chat application with seamless experience and smart conversation capabilities.
                          </p>

                          {[
                            {
                              label: 'GitHub',
                              href: 'https://github.com/rajput81325',
                              icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.58 2 12.26c0 4.54 2.87 8.39 6.84 9.75.5.09.68-.22.68-.49v-1.71c-2.78.62-3.37-1.37-3.37-1.37-.45-1.18-1.1-1.5-1.1-1.5-.9-.63.07-.62.07-.62 1 .07 1.53 1.05 1.53 1.05.89 1.56 2.34 1.11 2.91.85.09-.66.35-1.11.63-1.37-2.22-.26-4.56-1.14-4.56-5.07 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.3.1-2.71 0 0 .84-.28 2.75 1.05A9.38 9.38 0 0112 6.84c.85 0 1.7.12 2.5.34 1.91-1.33 2.75-1.05 2.75-1.05.55 1.41.2 2.45.1 2.71.64.72 1.03 1.63 1.03 2.75 0 3.94-2.34 4.81-4.57 5.06.36.32.68.94.68 1.9v2.82c0 .27.18.59.69.49A10.27 10.27 0 0022 12.26C22 6.58 17.52 2 12 2z" /></svg>
                            },
                            {
                              label: 'LinkedIn',
                              href: 'https://www.linkedin.com/in/aman-rajput-2b7680316/',
                              icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>
                            },
                            {
                              label: 'Email Support',
                              href: 'mailto:c84289798@gmail.com',
                              icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                            },
                            {
                              label: 'Privacy Policy',
                              href: '/privacy',
                              icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                            },
                            {
                              label: 'Terms of Services',
                              href: '/terms',
                              icon: <i className="bi bi-file-text"></i>
                            },
                            {
                              label: 'Cookie Policy',
                              href: '/cookies',
                              icon: <i className="bi bi-cookie"></i>
                            },
                            {
                              label: 'Documentation',
                              href: '/docs',
                              icon: <i className="bi bi-book"></i>
                            }
                          ].map((link, i, arr) => (
                            <a
                              key={i}
                              href={link.href}
                              target={link.href.startsWith('http') ? '_blank' : undefined}
                              rel="noopener noreferrer"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: window.innerWidth < 768 ? 'flex-start' : 'flex-start', // Always left aligned
                                gap: '10px',
                                padding: window.innerWidth < 768 ? '12px 0' : '13px 0',
                                borderBottom: i < arr.length - 1
                                  ? (theme === 'Dark' ? '0.5px solid rgba(255,255,255,0.08)' : '0.5px solid rgba(0,0,0,0.08)')
                                  : 'none',
                                color: theme === 'Dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                                fontSize: window.innerWidth < 768 ? '13px' : '14px',
                                textDecoration: 'none',
                                transition: 'color 0.15s',
                                width: '100%',
                              }}
                              onMouseEnter={e => e.currentTarget.style.color = theme === 'Dark' ? '#fff' : '#000'}
                              onMouseLeave={e => e.currentTarget.style.color = theme === 'Dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'}
                            >
                              <span style={{
                                opacity: 0.6,
                                display: 'flex',
                                flexShrink: 0,
                                width: '20px',
                                justifyContent: 'center'
                              }}>
                                {link.icon}
                              </span>
                              <span style={{
                                textAlign: 'left',
                                flex: 1
                              }}>
                                {link.label}
                              </span>
                            </a>
                          ))}
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>
            </div>
            {showSettingsModal && <div className="modal-backdrop fade show"></div>}

            {/* Remove Profile Picture Confirmation Modal */}
            <div
              className={`modal fade ${showRemoveProfileModal ? 'show' : ''}`}
              style={{
                display: showRemoveProfileModal ? 'block' : 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.5)'
              }}
              tabIndex="-1"
              onClick={(e) => {
                // Close modal when clicking outside the modal content
                if (e.target === e.currentTarget) {
                  setShowRemoveProfileModal(false);
                }
              }}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content bg-color3 rounded-4" style={{
                  border: 'none',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)'
                }}>
                  <div className="modal-header border-0 px-4 pt-4">
                    <h5 className="modal-title text-white">
                      <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                      Confirm Removal
                    </h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setShowRemoveProfileModal(false)}
                      aria-label="Close"
                    ></button>
                  </div>

                  <div className="modal-body px-4 py-3">
                    <p className="text-white mb-0">
                      Are you sure you want to remove your profile picture?
                    </p>
                    <small className="text-muted">
                      This action cannot be undone. You can upload a new picture anytime.
                    </small>
                  </div>

                  <div className="modal-footer border-0 px-4 pb-4">
                    <button
                      type="button"
                      className="btn btn-outline-light cancel-btn"
                      onClick={() => setShowRemoveProfileModal(false)}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={confirmRemoveProfilePic}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {showRemoveProfileModal && <div className="modal-backdrop fade show"></div>}

            {/* Help Modal */}
            <div
              className={`modal fade ${showHelpModal ? 'show' : ''}`}
              style={{
                display: showHelpModal ? 'block' : 'none',
                background: theme === 'Dark' ? 'rgba(26, 25, 25, 0.6)' : 'rgba(0, 0, 0, 0.3)',
                transition: 'opacity 0.3s ease'
              }}
              tabIndex="-1"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowHelpModal(false);
                }
              }}
            >
              <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px' }}>
                <div
                  className="modal-content rounded-4"
                  ref={helpModalRef}
                  style={{
                    border: 'none',
                    boxShadow: theme === 'Dark' ? '0 0 30px rgba(0, 0, 0, 0.5)' : '0 0 30px rgba(0, 0, 0, 0.15)',
                    backgroundColor: theme === 'Dark' ? '#1e1e1e' : '#ffffff',
                    transform: showHelpModal ? 'scale(1)' : 'scale(0.95)',
                    opacity: showHelpModal ? 1 : 0,
                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease'
                  }}
                >
                  <style>
                    {`
          /* Smooth accordion transitions */
          .help-accordion .accordion-collapse {
            transition: height 0.3s ease-in-out !important;
          }
          
          .help-accordion .accordion-button {
            transition: color 0.2s ease, border-radius 0.2s ease !important;
            background-color: transparent !important;
          }
          
          /* NO BACKGROUND COLOR ON HOVER */
          .help-accordion .accordion-button:hover {
            background-color: transparent !important;
          }
          
          .help-accordion .accordion-button:focus {
            box-shadow: none !important;
            background-color: transparent !important;
          }
          
          .help-accordion .accordion-button:not(.collapsed) {
            background-color: transparent !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
          }
          
          .help-accordion .accordion-body {
            transition: all 0.3s ease;
            animation: fadeIn 0.3s ease;
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* Dark theme accordion icon color - WHITE */
          body.theme-dark .help-accordion .accordion-button::after {
            filter: brightness(0) invert(1) !important;
            transition: transform 0.3s ease !important;
          }
          
          /* Light theme accordion styling */
          body.theme-light .help-accordion .accordion-item {
            background-color: #ffffff !important;
            border-color: #e0e0e0 !important;
          }
          
          body.theme-light .help-accordion .accordion-button {
            color: #1a1a1a !important;
            background-color: transparent !important;
          }
          
          body.theme-light .help-accordion .accordion-button:not(.collapsed) {
            color: #0d6efd !important;
            background-color: transparent !important;
          }
          
          body.theme-light .help-accordion .accordion-body {
            color: #000000 !important;
            background-color: #ffffff !important;
          }
          
          /* Dark theme accordion styling */
          body.theme-dark .help-accordion .accordion-item {
            background-color: #1e1e1e !important;
            border-color: #444 !important;
          }
          
          body.theme-dark .help-accordion .accordion-button {
            color: #fff !important;
            background-color: transparent !important;
          }
          
          body.theme-dark .help-accordion .accordion-button:not(.collapsed) {
            color: #4cc9f0 !important;
            background-color: transparent !important;
          }
          
          body.theme-dark .help-accordion .accordion-body {
            color: #ccc !important;
            background-color: #1e1e1e !important;
          }
          
          /* Smooth button transitions */
          .help-modal-btn {
            transition: all 0.2s ease !important;
          }
          
          .help-modal-btn:hover {
            transform: translateY(-1px);
          }
        `}
                  </style>

                  <div className="modal-header border-0 px-4 pt-4 pb-2">
                    <h5 className="modal-title d-flex align-items-center gap-2" style={{
                      color: theme === 'Dark' ? '#fff' : '#1a1a1a',
                      fontWeight: 500,
                      fontSize: '1.25rem'
                    }}>
                      <i className="bi bi-question-circle" style={{ color: theme === 'Dark' ? '#edf2f3' : '#151616' }}></i>
                      Help Center
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowHelpModal(false)}
                      aria-label="Close"
                      style={{
                        filter: theme === 'Dark' ? 'invert(1)' : 'none',
                        transition: 'transform 0.2s ease, opacity 0.2s ease',
                        opacity: 0.6
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.opacity = '0.6';
                      }}
                    ></button>
                  </div>

                  <div className="modal-body px-4 py-3">
                    <div className="mb-4">
                      <h6 className="mb-3" style={{
                        color: theme === 'Dark' ? '#e0e0e0' : '#333',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        letterSpacing: '0.3px'
                      }}>
                        Frequently Asked Questions
                      </h6>

                      <div className="accordion help-accordion" id="helpAccordion">
                        {/* Question 1 */}
                        <div className="accordion-item" style={{
                          backgroundColor: 'transparent',
                          border: theme === 'Dark' ? '1px solid #444' : '1px solid #e0e0e0',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease'
                        }}>
                          <h2 className="accordion-header">
                            <button
                              className="accordion-button"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#question1"
                              aria-expanded="true"
                              aria-controls="question1"
                              style={{
                                padding: '14px 16px',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                boxShadow: 'none',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <i className="bi bi-person-circle me-2" style={{ fontSize: '1.1rem' }}></i>
                              How do I change my profile photo?
                            </button>
                          </h2>
                          <div
                            id="question1"
                            className="accordion-collapse collapse show"
                            data-bs-parent="#helpAccordion"
                          >
                            <div className="accordion-body" style={{
                              padding: '16px',
                              fontSize: '0.9rem',
                              lineHeight: '1.6',
                              borderTop: theme === 'Dark' ? '1px solid #444' : '1px solid #e0e0e0',
                              color: theme === 'Dark' ? '#ccc' : '#000000'
                            }}>
                              <i className="bi bi-info-circle me-2" style={{ color: theme === 'Dark' ? '#4cc9f0' : '#0d6efd' }}></i>
                              You can change your profile photo in the Profile section of your Account Settings.
                            </div>
                          </div>
                        </div>

                        {/* Question 2 */}
                        <div className="accordion-item" style={{
                          backgroundColor: 'transparent',
                          border: theme === 'Dark' ? '1px solid #444' : '1px solid #e0e0e0',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease'
                        }}>
                          <h2 className="accordion-header">
                            <button
                              className="accordion-button collapsed"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#question3"
                              aria-expanded="false"
                              aria-controls="question3"
                              style={{
                                padding: '14px 16px',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                boxShadow: 'none',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <i className="bi bi-chat-dots me-2" style={{ fontSize: '1.1rem' }}></i>
                              How do I start a new conversation?
                            </button>
                          </h2>
                          <div
                            id="question3"
                            className="accordion-collapse collapse"
                            data-bs-parent="#helpAccordion"
                          >
                            <div className="accordion-body" style={{
                              padding: '16px',
                              fontSize: '0.9rem',
                              lineHeight: '1.6',
                              borderTop: theme === 'Dark' ? '1px solid #444' : '1px solid #e0e0e0',
                              color: theme === 'Dark' ? '#ccc' : '#000000'
                            }}>
                              <i className="bi bi-info-circle me-2" style={{ color: theme === 'Dark' ? '#4cc9f0' : '#0d6efd' }}></i>
                              Click the "New Chat" button in the sidebar to start a fresh conversation with Marshall AI.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-2">
                      <h6 className="mb-3" style={{
                        color: theme === 'Dark' ? '#e0e0e0' : '#333',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        letterSpacing: '0.3px'
                      }}>
                        Contact Support
                      </h6>
                      <a
                        href="mailto:r80081662@gmail.com?subject=Marshall%20AI%20Support&body=Hello,%0D%0A%0D%0AI%20need%20help%20with..."
                        className="btn help-modal-btn d-inline-flex align-items-center gap-2"
                        style={{
                          backgroundColor: 'transparent',
                          border: theme === 'Dark' ? '1px solid #eceff0' : '1px solid #1a1a1a',
                          color: theme === 'Dark' ? '#ecf3f5' : '#1a1a1a',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = theme === 'Dark' ? '#edf3f5' : '#1a1a1a';
                          e.currentTarget.style.color = theme === 'Dark' ? '#121213' : '#f7efef';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = theme === 'Dark' ? '#e2e9eb' : '#1a1a1a';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <i className="bi bi-envelope"></i>
                        Email Support
                      </a>

                      <div className="mt-3">
                        <small style={{
                          color: theme === 'Dark' ? '#888' : '#666',
                          fontSize: '0.8rem'
                        }}>
                          <i className="bi bi-clock me-1"></i>
                          We typically respond within 24 hours
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer border-0 px-4 pb-4 pt-2">
                    <button
                      type="button"
                      className="btn help-modal-btn"
                      onClick={() => setShowHelpModal(false)}
                      style={{
                        backgroundColor: theme === 'Dark' ? '#333' : '#e9ecef',
                        color: theme === 'Dark' ? '#fff' : '#1a1a1a',
                        borderRadius: '8px',
                        padding: '8px 24px',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        border: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = theme === 'Dark' ? '#444' : '#dee2e6';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = theme === 'Dark' ? '#333' : '#e9ecef';
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {showHelpModal && <div className="modal-backdrop fade show"></div>}

          </div>
        </header>

        {/* Messages Area */}
        {renderMessages()}

        {/* Input Area */}
        {renderInputArea()}
      </div>

      <CustomPopup
        isOpen={popupConfig.isOpen}
        onClose={closePopup}
        title={popupConfig.title}
        message={popupConfig.message}
        type={popupConfig.type}
        display={popupConfig.display}
      />

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        pauseOnHover={false}
        draggable
      />
    </div>
  );
}

export default Home;